import type { SessionUser } from '@raporto/shared'
import { ApiError, KIND_TEXT, kindFromStatus, isTransient, type FieldProblem } from './errors.js'
import { uuidv7 } from './ids.js'

/**
 * Eine frische Aktionskennung für eine Betreiberhandlung.
 *
 * `request()` weist JEDE schreibende Aktion ohne `idempotencyKey` ab – und
 * zwar bevor ein Netzaufruf entsteht. Genau daran sind die Betreiberrouten
 * gescheitert: kein Request, keine Zeile in der Konsole, aus Sicht des
 * Benutzers passierte nichts.
 *
 * Je Aufruf eine neue Kennung, nicht eine feste. Nach einem fehlenden
 * Sicherheitsnachweis läuft dieselbe Handlung ein zweites Mal – das ist ein
 * neuer Versuch und keine Wiederholung desselben, und mit einer festen
 * Kennung wäre der zweite Versuch für jede spätere Entdoppelung ein Duplikat
 * des gescheiterten ersten.
 */
const betreiberAktion = (): string => uuidv7()

/**
 * Die einzige Stelle im Web-Paket mit einem Netzwerkaufruf.
 *
 * Bildschirme lesen aus dem Cache und schreiben in den Ausgangskorb. Bräche
 * diese Regel, hätten wir in sechs Monaten fünfzehn Stellen mit eigener
 * Fehlerbehandlung, von denen keine zwei sich gleich verhalten – und genau
 * das ist die Ursache für "geklickt und nichts passiert".
 *
 * Der Test tests/web-single-fetch.test.ts scheitert, sobald irgendwo sonst
 * im Web-Paket fetch, XMLHttpRequest, EventSource, WebSocket oder
 * navigator.sendBeacon auftaucht.
 */

export const TIMEOUT_DEFAULT_MS = 15_000
/** Für PDF, Export und Versand ab Block 6 – schon hier vorgesehen. */
export const TIMEOUT_LONG_MS = 60_000

interface ApiConfig {
  /** Leer im Browser (gleicher Ursprung). In Tests die Adresse des Testservers. */
  baseUrl: string
}

const config: ApiConfig = { baseUrl: '' }

export function configureApi(next: Partial<ApiConfig>): void {
  Object.assign(config, next)
}

/**
 * Adresse des Mandantenlogos für ein img-Element.
 *
 * Ein Bild lädt der Browser selbst; hier entsteht nur die Adresse, damit die
 * Zusammensetzung nicht in einen Bildschirm wandert. Der Parameter v hängt am
 * Zeitpunkt der letzten Änderung: ohne ihn zeigt der Browser nach dem
 * Austausch weiter das alte Logo an.
 */
export function logoUrl(version: string | number): string {
  return `${config.baseUrl}/settings/logo?v=${encodeURIComponent(String(version))}`
}

/**
 * Im Browser verwaltet der Browser die Sitzungscookies. Ausserhalb – in Tests
 * unter Node – gibt es keinen Cookiespeicher, deshalb dieser minimale Ersatz.
 * Er ist bewusst hier und nicht anderswo: Transportdetails gehören in diese Datei.
 */
const isBrowser = typeof document !== 'undefined'
let cookieJar = ''

function applyCookies(headers: Headers): void {
  if (!isBrowser && cookieJar) headers.set('cookie', cookieJar)
}

function captureCookies(res: Response): void {
  if (isBrowser) return
  const raw = res.headers.getSetCookie?.() ?? []
  for (const entry of raw) {
    const pair = entry.split(';')[0]
    const name = pair.split('=')[0]
    const rest = cookieJar
      .split('; ')
      .filter((c) => c && !c.startsWith(`${name}=`))
      .join('; ')
    cookieJar = rest ? `${rest}; ${pair}` : pair
  }
}

export function clearCookieJar(): void {
  cookieJar = ''
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  /** Pflicht für alle schreibenden Aktionen. */
  idempotencyKey?: string
  timeoutMs?: number
  signal?: AbortSignal
  /**
   * Die Antwort ist eine Datei und keine JSON-Nachricht.
   *
   * Gebraucht für «Speichern unter …»: Der native Dialog will die Bytes,
   * nicht einen Verweis. Ein PDF durch `res.text()` zu schicken und wieder
   * zurückzuwandeln würde es beschädigen – deshalb eine eigene Abzweigung,
   * VOR dem Lesen des Textes.
   *
   * Es bleibt bei EINEM `fetch` in dieser Datei. Genau darum steht diese
   * Fahne hier und nicht ein zweiter Netzaufruf in `net/speichern.ts`: Die
   * Regel «nur apiClient.ts spricht mit dem Netz» hat einen Grund, und ein
   * Sonderfall ist der Anfang ihres Endes.
   */
  roh?: boolean
}

interface ServerErrorBody {
  error?: { code?: string; message?: string; details?: unknown }
}

/**
 * Der Grund aus `details.grund`, wenn dort einer steht.
 *
 * `details` traegt zwei verschiedene Dinge: eine Liste von Feldproblemen bei
 * einer misslungenen Pruefung, oder ein Objekt mit Zusatzangaben. Diese
 * Funktion holt sich nur den zweiten Fall und laesst den ersten in Ruhe.
 */
function grundAus(details: unknown): string | undefined {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return undefined
  const g = (details as { grund?: unknown }).grund
  return typeof g === 'string' ? g : undefined
}

/**
 * Die Angaben aus `details`, soweit es ein Objekt ist.
 *
 * Keine Deutung, keine Auswahl: Was der Server mitgibt, kommt durch. Die
 * Sperrseite im Anmeldefluss liest daraus ihre Daten – sie hat keine andere
 * Quelle, weil ein stillgelegter Betrieb nicht bis zu einer Sitzung kommt.
 */
function angabenAus(details: unknown): Record<string, unknown> | undefined {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return undefined
  return details as Record<string, unknown>
}

function parseFields(details: unknown): FieldProblem[] | undefined {
  if (!Array.isArray(details)) return undefined
  const fields = details.filter(
    (d): d is FieldProblem => typeof d === 'object' && d !== null && 'feld' in d && 'problem' in d
  )
  return fields.length ? fields : undefined
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const writing = method !== 'GET'
  const timeoutMs = options.timeoutMs ?? TIMEOUT_DEFAULT_MS

  if (writing && !options.idempotencyKey) {
    // Ein Programmierfehler, kein Benutzerfehler – deshalb hart und sofort.
    throw new ApiError('unknown', 'Schreibende Aktion ohne Aktionskennung.', {
      retryable: false,
      code: 'IDEMPOTENCY_KEY_MISSING'
    })
  }

  /*
    `accept` nur für die JSON-Wege.

    Bei einer Datei bleibt der Kopf weg, und der Browser schickt seinen
    eigenen. Hier stand ein Platzhalter mit Stern und Schrägstrich – und der
    enthält die Zeichenfolge, mit der ein Blockkommentar beginnt. Die
    Prüfung, die den einzigen `fetch` dieser Datei zählt, hielt von da an
    den halben Dateikörper für einen Kommentar und fand keinen. Ein
    Zeichenpaar, das eine Architekturregel blind macht.
  */
  const headers = new Headers()
  if (!options.roh) headers.set('accept', 'application/json')
  if (options.body !== undefined) headers.set('content-type', 'application/json')
  if (options.idempotencyKey) headers.set('Idempotency-Key', options.idempotencyKey)
  applyCookies(headers)

  // Jede Netzaktion hat eine Zeitgrenze. Ein Aufruf ohne Zeitgrenze ist die
  // häufigste Ursache für einen ewig drehenden Kreis.
  const controller = new AbortController()
  const timer = setTimeout(
    () => controller.abort(new DOMException('timeout', 'TimeoutError')),
    timeoutMs
  )
  if (options.signal) {
    options.signal.addEventListener('abort', () => controller.abort(options.signal?.reason), {
      once: true
    })
  }

  let res: Response
  try {
    res = await fetch(`${config.baseUrl}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: controller.signal
    })
  } catch (err) {
    clearTimeout(timer)
    const aborted = (err as Error)?.name === 'TimeoutError' || controller.signal.aborted
    const kind = aborted ? 'timeout' : 'network'
    throw new ApiError(kind, KIND_TEXT[kind], {
      retryable: true,
      // Bei einer Zeitüberschreitung kann die Aktion angekommen sein. Für
      // verbindliche Aktionen entscheidet darüber die Statusabfrage.
      unclear: kind === 'timeout' && writing
    })
  } finally {
    clearTimeout(timer)
  }

  captureCookies(res)

  /*
    Eine Datei wird als Datei gelesen – und nur im Erfolgsfall.

    Scheitert der Abruf, antwortet der Server mit JSON wie überall sonst;
    dann läuft der gewöhnliche Weg unten weiter und die Meldung kommt beim
    Betrieb an, statt als «Unbekannter Fehler» zu enden.
  */
  if (options.roh && res.ok) return (await res.blob()) as unknown as T

  const text = await res.text()
  let parsed: unknown = null
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      parsed = null
    }
  }

  if (res.ok) return parsed as T

  const body = (parsed ?? {}) as ServerErrorBody
  const kind = kindFromStatus(res.status)
  const message = body.error?.message ?? KIND_TEXT[kind]
  throw new ApiError(kind, message, {
    status: res.status,
    code: body.error?.code,
    fields: parseFields(body.error?.details),
    grund: grundAus(body.error?.details),
    angaben: angabenAus(body.error?.details),
    retryable: isTransient(res.status, kind)
  })
}

// ---------------------------------------------------------------------------
// Benannte Aufrufe – die Bildschirme sehen nur diese, nie request() direkt.
// ---------------------------------------------------------------------------

export interface SyncPayload {
  changeSeq: number
  bootstrap: boolean
  settings: Record<string, unknown> | null
  customers: Array<Record<string, unknown>>
  projects: Array<Record<string, unknown>>
  projectMembers: Array<Record<string, unknown>>
  catalogItems: Array<Record<string, unknown>>
  rapports: Array<Record<string, unknown>>
  documents: Array<Record<string, unknown>>
}

export interface ActionStatusPayload {
  clientActionId: string
  state: 'unbekannt' | 'laeuft' | 'ausgefuehrt'
  type: string | null
  httpStatus: number | null
  response: unknown
}

export interface TenantUser {
  userId: string
  name: string
  /**
   * Die Mailadresse der Person, nicht die der Firma.
   *
   * Daran hängen Anmeldung und zweiter Faktor. Die offizielle Adresse des
   * Betriebs – die, die auf der Rechnung steht – liegt in den Einstellungen
   * und ist etwas anderes.
   */
  email: string
  /** Die Nummer der Person. Steht auf keinem Beleg. */
  phone: string | null
  role: 'superuser' | 'worker'
  /** Hat den Betrieb verlassen. Nicht gelöscht – an ihr hängen Rapporte. */
  deaktiviert: boolean
  zweiterFaktor: boolean
  /** Eingeladen, aber noch nie angemeldet. */
  einladungOffen: boolean
}

export interface Einrichtungspunkt {
  id: string
  text: string
  stand: 'erledigt' | 'fehlt' | 'empfohlen' | 'blockierend'
  hinweis?: string
}

export interface Einrichtungsstand {
  bereiche: Array<{ id: string; titel: string; punkte: Einrichtungspunkt[] }>
  blockierend: number
  offen: number
  empfohlen: number
  erledigt: number
  bereit: boolean
}

/**
 * Was bei einem verschickten Link zurückkommt.
 *
 * `link` ist gesetzt, solange kein echter Mailanbieter angeschlossen ist –
 * dann kann der Betrieb ihn von Hand weitergeben. Sobald wirklich zugestellt
 * wird, ist er `null` und verlässt den Server nur noch per Mail an den
 * Empfänger selbst.
 */
export interface ImportVorschau {
  zuordnung: Array<{ spalte: number; ueberschrift: string; feld: string | null }>
  zeilen: Array<{
    zeile: number
    daten: Record<string, string>
    fehler: string[]
    dublette: { id: string; name: string; grund: string } | null
  }>
  gelesen: number
  gueltig: number
  fehlerhaft: number
  dubletten: number
  felder: Array<{ id: string; label: string }>
}

export interface ZugangAntwort {
  link: string | null
  zustellungEcht: boolean
  gueltigBis: string
  versandFehler: string | null
}

/* ═══════════════════════════════════════════════════════════════════════════
   Mein Raporto · das eigene Abo
   ═══════════════════════════════════════════════════════════════════════════ */

export type Kundenhandlung = 'abo_starten' | 'paket_wechseln' | 'kuendigen' | 'kuendigung_zurueck'

/**
 * Woran es liegt, dass ein Betrieb im Ausstiegsfenster steht.
 *
 * Die drei Fälle dürfen nie vermischt werden: Eine offene Zahlung ist keine
 * Kündigung, und der Satz auf dem Bildschirm hängt daran.
 */
export type Ausstiegsgrund = 'test_abgelaufen' | 'zahlung_offen' | 'abo_beendet'

/** Wo im Fenster der Betrieb gerade steht. */
export type Ausstiegslage = 'laeuft' | 'ausstieg' | 'gesperrt'

export interface Ausstieg {
  grund: Ausstiegsgrund
  /** Tag 0 – das Ende der Test- oder der bezahlten Periode. */
  endeAm: string
  /**
   * Der Tag, ab dem die normale Nutzung gesperrt ist – oder `null`.
   *
   * `null` heisst NICHT «es gibt keinen». Es heisst: Der Server nennt keinen,
   * weil für diesen Fall nicht gesperrt wird. Dann darf die Oberfläche auch
   * keine Sperre ankündigen – eine Warnung, nach der nichts passiert, kostet
   * das Vertrauen in die nächste.
   */
  sperreAm: string | null
  tageBisSperre: number | null
  /** `null` heisst: Dazu sagen wir noch nichts. NICHT «es gibt keines». */
  loeschungVorgesehenAm: string | null
  tageBisLoeschung: number | null
  tageBisEnde: number
  lage: Ausstiegslage
  /**
   * Die Phase des Ausstiegs – vom Server benannt, nicht hier abgeleitet.
   *
   * `laeuft` Frist läuft · `nachfrist` Zahlung offen, Nutzung unverändert ·
   * `lesemodus` nur noch lesen und exportieren · `geloescht` die Frist ist
   * abgelaufen. Die Oberfläche rechnet daraus nichts aus; sie zeigt an, was
   * dasteht.
   */
  phase: 'laeuft' | 'nachfrist' | 'lesemodus' | 'geloescht'
  /** Der Tag, an dem abgebucht wird – `null`, wenn keiner feststeht. */
  abbuchungAm: string | null
  /**
   * Läuft das Abo am Enddatum aus, statt sich zu verlängern?
   *
   * Unterscheidet die beiden Lagen, in denen `abbuchungAm` leer ist: «kein
   * Zahlungsmittel hinterlegt, bitte etwas tun» und «gekündigt, es ist alles
   * geregelt».
   */
  verlaengertNicht: boolean
}

export interface AboStand {
  paket: { key: string | null; name: string | null; preisRp: number | null }
  vertrag: {
    text: string
    erklaerung: string
    beginn: string | null
    gekuendigtAm: string | null
    wirksamPer: string | null
  }
  nutzung: {
    planKey: string | null
    paketName: string | null
    benutzer: number
    benutzerGrenze: number | null
    speicher: { gbBelegt: number; gbGrenze: number; prozent: number }
  }
  branding: { sichtbar: boolean; addonBuchbar: boolean; addonPreisRp: number }
  handlungen: Kundenhandlung[]
  abrechnung: { verrechnet: boolean; hinweis: string }
  /**
   * Das Ausstiegsfenster – oder `null`, wenn der Betrieb in keinem steht.
   *
   * Vom Server gerechnet und hier NUR gelesen. Die Oberfläche rechnet keinen
   * dieser Tage selbst aus: Sonst nennt der Hinweis im Tool eines Tages einen
   * anderen Tag als die Mail und als die Automatik, die wirklich sperrt.
   *
   * `loeschungVorgesehenAm` ist `null`, solange der Server das Datum nicht
   * herausgibt – und dann wird es auch nicht angezeigt. Ein Löschdatum auf
   * dem Bildschirm ist eine Zusage.
   */
  ausstieg: Ausstieg | null
  pakete: Array<{
    id: string
    name: string
    fuerWen: string
    preisRp: number
    benutzer: number
    speicherGb: number
    kern: string
    merkmale: string[]
    empfohlen?: boolean
    aktuell: boolean
    brandingText: string
  }>
  /** Ob die verändernden Handlungen am Server überhaupt offen sind. */
  selbstbedienung: boolean
  /**
   * Der Zahlungsstand – von Stripe geschrieben, hier nur gelesen.
   *
   * `eingerichtet: false` heisst: Auf diesem Stand gibt es keinen
   * Stripe-Schlüssel. Dann verhält sich der Bildschirm wie bisher und
   * verspricht keine Buchung, die er nicht einlösen kann.
   */
  zahlung: {
    eingerichtet: boolean
    status: 'kein_abo' | 'checkout_offen' | 'aktiv' | 'zahlung_offen' | 'beendet'
    bezahltesPaket: string | null
    intervall: 'monat' | 'jahr' | null
    periodeBis: string | null
    kuendigungZumEnde: boolean
    /**
     * Läuft gerade die kostenlose Testphase?
     *
     * `status: 'aktiv'` fasst Stripes `active` und `trialing` zusammen – für
     * die Frage «darf gearbeitet werden» ist das richtig. Für die Frage «ist
     * bezahlt» ist es falsch, und genau die beantwortet dieses Feld.
     */
    inTestphase: boolean
    portalMoeglich: boolean
  }
}

export type Abointervall = 'monat' | 'jahr'

/**
 * Was eine Bestellung ausmacht – einmal benannt.
 *
 * Als eigener Typ und nicht inline im Aufruf: Zwei Wege schicken denselben
 * Körper (mit und ohne Zahlung), und ein `Parameters<typeof api.…>` an der
 * zweiten Stelle wäre ein Selbstbezug, an dem TypeScript den ganzen Klienten
 * zu `any` erklärt – mit zwanzig Folgefehlern in Dateien, die damit nichts zu
 * tun haben.
 */
export interface Bestellung {
  paket: string
  branche: string
  firma: {
    name: string
    street: string
    zip: string
    city: string
    uid: string
    /* Die Adresse des BETRIEBS – nicht die der Person, die sich anmeldet. */
    email?: string
    phone?: string
  }
  person: { name: string; email: string; passwort: string; phone: string }
  /* Nur gesetzt, solange der Betreiber die Registrierung hinter einem Code hält. */
  code?: string
  /* Nur gesetzt, wenn der Betrieb aus einer anderen Software kommt. */
  nummernkreis?: {
    naechsteRechnungsnummer?: number
    naechsteOffertnummer?: number
    jaehrlich?: boolean
  }
  /*
    Der Schlüssel aus einer Einladung in eine Kita-Gemeinschaft.

    Freiwillig. Mit ihm gehört der neue Betrieb nach dem Abschluss zur
    Gemeinschaft – eingelöst wird die Einladung erst, wenn der Betrieb
    entsteht. Ein abgebrochener Checkout hinterlässt also nichts.
  */
  einladung?: string
}

export const api = {
  /**
   * Erster Schritt der Anmeldung.
   *
   * Bei einem Konto mit zweitem Faktor kommt hier KEINE Sitzung zurück,
   * sondern nur die Auskunft, dass ein Code fehlt. Das Cookie ist trotzdem
   * gesetzt – der zweite Schritt hängt daran.
   */
  login: (email: string, password: string) =>
    request<{ session: unknown; zweiterFaktorNoetig: boolean }>('/auth/login', {
      method: 'POST',
      body: { email, password },
      idempotencyKey: 'login' // Anmeldung ist keine Geschäftsaktion, aber der Kopf schadet nicht
    }),

  /**
   * Das eigene Passwort ändern.
   *
   * Der Server verlangt das bisherige mit – ein übernommenes Gerät soll das
   * Passwort nicht stillschweigend austauschen können. Und er beendet danach
   * alle anderen Sitzungen; die Zahl kommt zurück, damit die Oberfläche sie
   * zeigen kann.
   */
  passwortAendern: (current: string, next: string, idempotencyKey: string) =>
    request<{ ok: true; beendeteSitzungen: number }>('/security/password', {
      method: 'POST',
      body: { current, next },
      idempotencyKey
    }),

  /** Zweiter Schritt: Code aus der App oder ein Wiederherstellungscode. */
  zweiterFaktor: (code: string) =>
    request<{
      ok: true
      recoveryVerbraucht: boolean
      verbleibendeCodes: number
      hinweis: string | null
    }>('/auth/2fa', { method: 'POST', body: { code }, idempotencyKey: 'zweiter-faktor' }),

  // -------------------------------------------------------------------------
  // Sicherheit des eigenen Kontos
  // -------------------------------------------------------------------------

  securityStatus: () =>
    request<{
      twoFactorEnabled: boolean
      twoFactorRequired: boolean
      sensitiveAllowed: boolean
      setupLaeuft: boolean
      wiederherstellungscodesOffen: number
    }>('/security/status'),

  /** Startet die Einrichtung. Die Antwort enthält das Geheimnis – nur hier. */
  zweiFaktorSetup: (idempotencyKey: string) =>
    request<{ otpauthUri: string; qrSvg: string; manuellerSchluessel: string; hinweis: string }>(
      '/security/2fa/setup',
      { method: 'POST', body: {}, idempotencyKey }
    ),

  zweiFaktorBestaetigen: (code: string, idempotencyKey: string) =>
    request<{ enabled: true; recoveryCodes: string[]; hinweis: string }>('/security/2fa/confirm', {
      method: 'POST',
      body: { code },
      idempotencyKey
    }),

  zweiFaktorAbschalten: (password: string, code: string, idempotencyKey: string) =>
    request<{ enabled: false; beendeteSitzungen: number }>('/security/2fa/disable', {
      method: 'POST',
      body: { password, code },
      idempotencyKey
    }),

  neueWiederherstellungscodes: (code: string, idempotencyKey: string) =>
    request<{ recoveryCodes: string[] }>('/security/2fa/recovery-codes', {
      method: 'POST',
      body: { code },
      idempotencyKey
    }),

  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST', idempotencyKey: 'logout' }),

  me: () =>
    request<{
      session: unknown
      settings: unknown
      zweiterFaktorNoetig: boolean
      name?: string
      /** Betreibt dieses Konto Raporto selbst? Nur dann gibt es den Menüpunkt. */
      betreiber?: boolean
      /**
       * Sitzt diese Sitzung auf dem Plattformbetrieb?
       *
       * Dann gibt es «Mein Raporto» nicht: Der Bereich gehört einem Kunden,
       * und der Plattformbetrieb ist keiner. Getrennt von `betreiber` – das
       * eine sagt, wer das Konto ist, das andere, worauf es gerade sitzt.
       */
      plattformbetrieb?: boolean
      /**
       * Wie lange die Sitzung vor dieser Anfrage unbenutzt war.
       *
       * Die zweite Quelle für den Sperrbildschirm – die erste liegt in der
       * IndexedDB und kann mit den Websitedaten verschwinden.
       */
      inaktivMs?: number
    }>('/me'),

  /**
   * Das Betreibercockpit – alle Betriebe mit ihren Zahlen.
   *
   * Geschützt am Server (`requireBetreiber`); ein normaler Betrieb bekommt
   * 403, egal was die Oberfläche anzeigt.
   */
  betreiberMandanten: (mitZahlen = true, suche = '') => {
    const p = new URLSearchParams({ mitZahlen: mitZahlen ? 'ja' : 'nein' })
    if (suche.trim()) p.set('suche', suche.trim())
    return request<BetreiberListe>(`/betreiber/mandanten?${p.toString()}`)
  },

  betreiberMandant: (id: string) => request<BetreiberMandant>(`/betreiber/mandanten/${id}`),
  /* Paket 12 · Der Einstiegsbildschirm der Control Plane. Nur lesend. */
  betreiberUebersicht: () => request<BetreiberUebersicht>('/betreiber/uebersicht'),

  /**
   * Die wirtschaftliche Lage – eigene Abfrage, weil sie bei Stripe nachliest.
   *
   * Sie steht neben `betreiberUebersicht` und nicht darin: Die Übersicht misst
   * den Speicher jedes Betriebs und ist die langsamste Abfrage im Programm.
   * Zusammengelegt wartete die eine auf die andere.
   */
  betreiberWirtschaft: (zeitraum: BetreiberZeitraum = 'monat') =>
    request<BetreiberWirtschaft>(`/betreiber/wirtschaft?zeitraum=${zeitraum}`),
  /* Paket 13 · Testbetriebe. Nur für die Plattformrolle OWNER. */
  betreiberPurgeUmfang: (id: string) =>
    request<BetreiberPurgeUmfang>(`/betreiber/mandanten/${id}/purge-umfang`),
  betreiberTestmarkierung: (id: string, wert: boolean, grund: string) =>
    request<{ ok: boolean; geaendert: boolean; istTestTenant: boolean }>(
      `/betreiber/mandanten/${id}/testbetrieb`,
      { method: 'POST', body: { wert, grund }, idempotencyKey: betreiberAktion() }
    ),
  betreiberPurge: (id: string, bestaetigung: string, grund: string) =>
    request<BetreiberPurgeErgebnis>(`/betreiber/mandanten/${id}/purge`, {
      method: 'POST',
      body: { bestaetigung, grund },
      idempotencyKey: betreiberAktion()
    }),
  /*
    Paket D8 · Die Sofortloeschung eines KUNDENBETRIEBS.

    `verstanden` ist die nicht vorausgewaehlte Checkbox und steht deshalb im
    Koerper und nicht im Pfad: Der Server prueft sie (`z.literal(true)`), und
    eine Oberflaeche, die sie weglaesst, kommt nicht durch.
  */
  betreiberSofortloeschung: (
    id: string,
    bestaetigung: string,
    grund: string,
    verstanden: boolean
  ) =>
    request<BetreiberSofortloeschung>(`/betreiber/mandanten/${id}/sofortloeschung`, {
      method: 'POST',
      body: { bestaetigung, grund, verstanden },
      idempotencyKey: betreiberAktion()
    }),
  /* Paket 10 · Paketwechsel. Nur für die Plattformrolle OWNER. */
  betreiberPaketVorschau: (id: string, ziel: string) =>
    request<BetreiberPaketVorschau>(
      `/betreiber/mandanten/${id}/paket-vorschau?ziel=${encodeURIComponent(ziel)}`
    ),
  betreiberPaketWechseln: (id: string, ziel: string, grund: string) =>
    request<BetreiberPaketErgebnis>(`/betreiber/mandanten/${id}/paket`, {
      method: 'POST',
      body: { ziel, grund },
      idempotencyKey: betreiberAktion()
    }),

  /* ----- Handlungen des Betreibers. Alle vier sind geschützt und protokolliert. ----- */

  /** Paket 4 · Gilt gerade ein Sicherheitsnachweis? Reine Auskunft. */
  betreiberStepUpStand: () =>
    request<{ gueltig: boolean; restSekunden: number; fensterMinuten: number }>(
      '/betreiber/step-up'
    ),

  /** Paket 4 · Einen Sicherheitsnachweis erbringen. */
  betreiberStepUp: (code: string) =>
    request<{ ok: true; restSekunden: number }>('/betreiber/step-up', {
      method: 'POST',
      body: { code },
      idempotencyKey: betreiberAktion()
    }),

  /**
   * Den Vertragsstatus eines Betriebs setzen – die Einstufung.
   *
   * `ziel` ist einer der Werte aus `vertrag.moeglicheZiele` des Detailabrufs.
   * Die Liste kommt vom Server, weil dort die Übergangstabelle steht; hier
   * steht keine zweite.
   */
  /* ---- Mein Raporto · das eigene Abo -------------------------------------
     Keine dieser Adressen trägt eine Betriebskennung. Der Betrieb ist am
     Server immer der aus der Sitzung; hier gibt es nichts, was man vertauschen
     könnte. ----------------------------------------------------------------- */

  abo: () => request<AboStand>('/abo'),

  aboVorschau: (paket: string) =>
    request<BetreiberPaketVorschau>(`/abo/vorschau?paket=${encodeURIComponent(paket)}`),

  aboPaket: (paket: string, idempotencyKey: string) =>
    request<{ geaendert: boolean; von: string | null; nach: string | null }>('/abo/paket', {
      method: 'POST',
      body: { paket },
      idempotencyKey
    }),

  aboStarten: (idempotencyKey: string) =>
    request<{ geaendert: boolean }>('/abo/starten', { method: 'POST', body: {}, idempotencyKey }),

  aboKuendigen: (grund: string, idempotencyKey: string) =>
    request<{ geaendert: boolean }>('/abo/kuendigen', {
      method: 'POST',
      body: { grund },
      idempotencyKey
    }),

  aboKuendigungZurueck: (idempotencyKey: string) =>
    request<{ geaendert: boolean }>('/abo/kuendigung-zuruecknehmen', {
      method: 'POST',
      body: {},
      idempotencyKey
    }),

  /*
    Bezahlen.

    Beide geben eine Adresse bei Stripe zurück und ändern in Raporto nichts.
    Hier wird bewusst KEINE Preis-Kennung mitgeschickt: Der Server löst sie
    aus Paket und Intervall auf, und was am Ende gilt, liest er nach der
    Zahlung aus der Subscription. Ein Browser, der einen Preis nennen dürfte,
    dürfte sich sein Paket aussuchen.
  */
  aboCheckout: (paket: string, intervall: Abointervall, idempotencyKey: string) =>
    request<{ url: string; id: string }>('/abo/checkout', {
      method: 'POST',
      body: { paket, intervall },
      idempotencyKey
    }),

  aboPortal: (idempotencyKey: string) =>
    request<{ url: string }>('/abo/portal', { method: 'POST', body: {}, idempotencyKey }),

  betreiberVertragsstatus: (id: string, ziel: string, grund: string) =>
    request<{
      ok: boolean
      geaendert: boolean
      von: string
      nach: string
      moeglicheZiele: string[]
    }>(`/betreiber/mandanten/${id}/vertragsstatus`, {
      method: 'POST',
      body: { ziel, grund },
      idempotencyKey: betreiberAktion()
    }),

  betreiberSperren: (id: string, grund: string) =>
    request<{ ok: true; beendeteSitzungen: number; hinweis: string }>(
      `/betreiber/mandanten/${id}/sperren`,
      { method: 'POST', body: { grund }, idempotencyKey: betreiberAktion() }
    ),

  betreiberEntsperren: (id: string, grund = '') =>
    request<{ ok: true; hinweis: string }>(`/betreiber/mandanten/${id}/entsperren`, {
      method: 'POST',
      body: { grund },
      idempotencyKey: betreiberAktion()
    }),

  /** Merkt nur vor. Es wird nichts gelöscht – siehe die Route. */
  betreiberLoeschungVormerken: (id: string, grund: string, bestaetigung: string) =>
    request<{ ok: true; hinweis: string }>(`/betreiber/mandanten/${id}/loeschung-vormerken`, {
      method: 'POST',
      body: { grund, bestaetigung },
      idempotencyKey: betreiberAktion()
    }),

  betreiberLoeschungZuruecknehmen: (id: string) =>
    request<{ ok: true }>(`/betreiber/mandanten/${id}/loeschung-vormerken`, {
      method: 'DELETE',
      idempotencyKey: betreiberAktion()
    }),

  /** Der Link geht per Mail an den Administrator und steht nicht in der Antwort. */
  betreiberZugangslink: (id: string) =>
    request<{ ok: true; an: string; gueltigBis: string; versandFehler: string | null }>(
      `/betreiber/mandanten/${id}/zugangslink`,
      { method: 'POST', body: {}, idempotencyKey: betreiberAktion() }
    ),

  betreiberProtokoll: (grenze = 50) =>
    request<BetreiberProtokoll>(`/betreiber/protokoll?grenze=${grenze}`),

  bootstrap: () => request<SyncPayload>('/sync/bootstrap'),

  /**
   * Die Liste der Personen im Betrieb. Bewusst kein Cache: nur ein Superuser
   * darf sie sehen, und sie ändert selten genug, dass ein Aufruf beim Öffnen
   * des Formulars reicht.
   */
  listUsers: () => request<{ users: TenantUser[] }>('/users'),

  /**
   * Was zur Einrichtung noch fehlt.
   *
   * Bewusst kein Cache: der Zustand ändert sich mit jeder Eingabe auf den
   * Nachbarreitern, und eine gespeicherte Antwort wäre genau die
   * Fortschrittsanzeige, die dieser Bildschirm nicht sein soll.
   */
  einrichtung: () => request<Einrichtungsstand>('/einrichtung'),

  /** Die Kundenliste ansehen – liest, prüft und schreibt nichts. */
  kundenimportVorschau: (inhalt: string, zuordnung: Record<string, string | null>) =>
    request<ImportVorschau>('/customers/import/vorschau', {
      method: 'POST',
      body: { inhalt, zuordnung },
      idempotencyKey: 'import-vorschau',
      timeoutMs: TIMEOUT_LONG_MS
    }),

  /** Und danach, nach Bestätigung, schreiben. */
  kundenimport: (
    inhalt: string,
    zuordnung: Record<string, string | null>,
    dublettenTrotzdem: boolean,
    idempotencyKey: string
  ) =>
    request<{
      geschrieben: number
      uebersprungen: number
      gelesen: number
      fehlerhaft: number
      dubletten: number
    }>('/customers/import', {
      method: 'POST',
      body: { inhalt, zuordnung, dublettenTrotzdem },
      idempotencyKey,
      timeoutMs: TIMEOUT_LONG_MS
    }),

  /** Welche Startkataloge Raporto mitliefert. */
  katalogVorlagen: () =>
    request<{
      vorlagen: Array<{
        key: string
        industry: string
        label: string
        positionen: number
        ohnePreis: number
      }>
    }>('/catalog-templates'),

  /**
   * Eine Vorlage in den eigenen Katalog übernehmen.
   *
   * Ergänzt, ersetzt nie: was der Betrieb schon hat, bleibt unangetastet –
   * auch wenn es anders heisst oder anders kostet als die Vorlage.
   */
  katalogVorlageUebernehmen: (key: string, idempotencyKey: string) =>
    request<{ uebernommen: number; uebersprungen: number; ohnePreis: number }>(
      `/catalog-templates/${encodeURIComponent(key)}`,
      { method: 'POST', idempotencyKey }
    ),

  /* ------------------------------------------------- Selbstverwaltung */
  /**
   * Einen Rücksetzlink anfordern.
   *
   * Die Antwort ist immer dieselbe – auch wenn es die Adresse nicht gibt. Die
   * Oberfläche darf deshalb nie «Adresse unbekannt» anzeigen; sie hat die
   * Auskunft nicht und soll sie nicht haben.
   */
  passwortVergessen: (email: string) =>
    request<{ ok: boolean; hinweis: string; zustellungEcht: boolean }>('/auth/passwort-vergessen', {
      method: 'POST',
      body: { email },
      idempotencyKey: 'pw-vergessen'
    }),

  /**
   * Einen Anmeldelink anfordern.
   *
   * Nur verfügbar, wenn der Betreiber ihn eingeschaltet hat
   * (RAPORTO_MAGIC_LINK). Sonst antwortet der Server mit 404 – eine
   * abgeschaltete Funktion soll nicht verraten, dass es sie gibt.
   */
  anmeldelinkAnfordern: (email: string) =>
    request<{ ok: boolean; hinweis: string; zustellungEcht: boolean }>('/auth/anmeldelink', {
      method: 'POST',
      body: { email },
      idempotencyKey: 'anmeldelink'
    }),

  /** Den Anmeldelink einlösen. Verbraucht den Token. */
  anmeldelinkEinloesen: (token: string) =>
    request<{ zweiterFaktorNoetig: boolean; session: SessionUser | null }>('/auth/anmelden', {
      method: 'POST',
      body: { token },
      idempotencyKey: 'anmelden'
    }),

  /** Gilt dieser Link noch? Fragt nach, ohne ihn zu verbrauchen. */
  zugangPruefen: (art: 'passwort' | 'einladung', token: string) =>
    request<{ gueltig: boolean; name: string; email?: string }>(
      `/auth/${art === 'passwort' ? 'passwort-neu' : 'einladung'}?token=${encodeURIComponent(token)}`
    ),

  passwortNeuSetzen: (token: string, passwort: string) =>
    request<{ ok: boolean; beendeteSitzungen: number }>('/auth/passwort-neu', {
      method: 'POST',
      body: { token, passwort },
      idempotencyKey: 'pw-neu'
    }),

  einladungAnnehmen: (token: string, passwort: string) =>
    request<{ ok: boolean; email: string }>('/auth/einladung', {
      method: 'POST',
      body: { token, passwort },
      idempotencyKey: 'einladung'
    }),

  /**
   * Der Nachweis für folgenreiche Handlungen.
   *
   * `code` nur, wenn die handelnde Person einen zweiten Faktor eingerichtet
   * hat – der Server verlangt ihn dann und nur dann.
   */
  benutzerEinladen: (
    daten: {
      name: string
      email: string
      role: 'superuser' | 'worker'
      phone?: string
      passwort?: string
      code?: string
    },
    idempotencyKey: string
  ) =>
    request<ZugangAntwort & { userId: string }>('/users', {
      method: 'POST',
      body: daten,
      idempotencyKey
    }),

  rolleSetzen: (
    userId: string,
    role: 'superuser' | 'worker',
    nachweis: { passwort: string; code?: string },
    idempotencyKey: string
  ) =>
    request<{ ok: boolean; role: string; beendeteSitzungen?: number }>(`/users/${userId}/rolle`, {
      method: 'POST',
      body: { role, ...nachweis },
      idempotencyKey
    }),

  /** Nachweis nötig, sobald die Person volle Rechte hat – der Server entscheidet. */
  benutzerDeaktivieren: (
    userId: string,
    nachweis: { passwort: string; code?: string } | null,
    idempotencyKey: string
  ) =>
    request<{ ok: boolean; beendeteSitzungen: number }>(`/users/${userId}/deaktivieren`, {
      method: 'POST',
      body: nachweis ?? {},
      idempotencyKey
    }),

  benutzerReaktivieren: (userId: string, idempotencyKey: string) =>
    request<{ ok: boolean }>(`/users/${userId}/reaktivieren`, { method: 'POST', idempotencyKey }),

  /**
   * Aus dem Betrieb entfernen – oder eine Einladung zurückziehen.
   *
   * Eine Handlung, zwei Sätze: Der Server sagt in `art`, ob die Person je
   * angemeldet war. Danach richtet sich die Meldung, nicht der Aufruf.
   *
   * Nachweis nötig, sobald die Person volle Rechte hat – das entscheidet der
   * Server, nicht die Oberfläche.
   */
  benutzerEntfernen: (
    userId: string,
    nachweis: { passwort: string; code?: string } | null,
    idempotencyKey: string
  ) =>
    request<{
      ok: boolean
      beendeteSitzungen: number
      art: 'einladung' | 'mitglied'
      kontoEntfernt: boolean
    }>(`/users/${userId}/entfernen`, {
      method: 'POST',
      body: nachweis ?? {},
      idempotencyKey
    }),

  passwortLinkSenden: (
    userId: string,
    nachweis: { passwort: string; code?: string },
    idempotencyKey: string
  ) =>
    request<ZugangAntwort>(`/users/${userId}/passwort-link`, {
      method: 'POST',
      body: nachweis,
      idempotencyKey
    }),

  /** Der zweite Faktor einer anderen Person – mit eigenem Passwort und Grund. */
  zweitenFaktorZuruecksetzen: (
    userId: string,
    nachweis: { passwort: string; code?: string },
    grund: string,
    idempotencyKey: string
  ) =>
    request<{ ok: boolean; beendeteSitzungen: number }>(
      `/users/${userId}/zweiter-faktor-zuruecksetzen`,
      { method: 'POST', body: { ...nachweis, grund }, idempotencyKey }
    ),

  /**
   * Logo setzen und entfernen.
   *
   * Der Schlüssel wird nicht mitgeschickt – der Server bildet ihn. Die
   * Oberfläche schickt nur die Bilddaten und bekommt die neuen Einstellungen
   * zurück, damit die Anzeige nicht raten muss, ob es geklappt hat.
   */
  setLogo: (image: string, filename: string, idempotencyKey: string) =>
    request<{ settings: Record<string, unknown>; format: string; bytes: number }>(
      '/settings/logo',
      { method: 'PUT', body: { image, filename }, idempotencyKey, timeoutMs: 30_000 }
    ),

  removeLogo: (idempotencyKey: string) =>
    request<{ settings: Record<string, unknown> }>('/settings/logo', {
      method: 'DELETE',
      idempotencyKey
    }),

  changes: (since: number) => request<SyncPayload>(`/sync/changes?since=${since}`),

  /** Löst den Zustand "unklar" auf – nie blind wiederholen. */
  actionStatus: (clientActionId: string) =>
    request<ActionStatusPayload>(`/actions/${clientActionId}`),

  /** Generischer Weg für Ausgangskorb-Einträge. */
  send: <T>(
    path: string,
    method: 'POST' | 'PATCH' | 'PUT',
    body: unknown,
    idempotencyKey: string,
    timeoutMs?: number
  ) => request<T>(path, { method, body, idempotencyKey, timeoutMs }),

  listRapports: (filter: RapportFilter = {}) => {
    const p = new URLSearchParams()
    p.set('status', filter.status ?? 'offen')
    if (filter.projectId) p.set('projectId', filter.projectId)
    if (filter.userId) p.set('userId', filter.userId)
    if (filter.signed) p.set('signed', filter.signed)
    if (filter.priceOpen) p.set('priceOpen', 'ja')
    if (filter.withPhotos) p.set('withPhotos', 'ja')
    return request<{ rapports: OfficeRapport[] }>(`/rapports?${p.toString()}`)
  },

  getRapport: (id: string) =>
    request<{
      rapport: Record<string, unknown>
      lines: Array<Record<string, unknown>>
      photos: Array<Record<string, unknown>>
    }>(`/rapports/${id}`),

  /**
   * Fotos gehen einzeln und nicht über den Ausgangskorb: ein Rapport mit zehn
   * Fotos wäre ein Eintrag von mehreren Megabyte, und bei schlechtem Netz geht
   * der nie durch.
   */
  uploadPhoto: (
    rapportId: string,
    body: { clientPhotoId: string; image: string; width?: number; height?: number }
  ) =>
    request<{ photo: { id: string; alreadyThere: boolean } }>(`/rapports/${rapportId}/photos`, {
      method: 'POST',
      body,
      idempotencyKey: body.clientPhotoId,
      timeoutMs: TIMEOUT_LONG_MS
    }),

  /**
   * Löschen ist bewusst kein Ausgangskorb-Eintrag: ob gelöscht oder
   * archiviert wird, entscheidet der Server anhand der Belege.
   */
  remove: <T>(path: string, idempotencyKey: string) =>
    request<T>(path, { method: 'DELETE', idempotencyKey }),

  // -------------------------------------------------------------------------
  // Belege (Block 5)
  //
  // Alle schreibenden Aufrufe hier gehen direkt zum Server und NICHT über den
  // Ausgangskorb. Das ist Absicht: eine Rechnung im Entwurf ist eine Handlung
  // der Klasse B – verbindlich, mit einer Sperre dahinter, die nur der Server
  // kennt. Würde das Büro offline Zeilen in Entwürfe ziehen und die Sperre
  // fiele erst beim Übertragen auf, stünden zwei halbfertige Rechnungen da und
  // jemand müsste raten, welche gilt. Im Büro gibt es Netz; auf der Baustelle
  // zählt Offline, hier zählt Eindeutigkeit.
  // -------------------------------------------------------------------------

  openLines: (filter: { projectId?: string; customerId?: string } = {}) => {
    const p = new URLSearchParams()
    if (filter.projectId) p.set('projectId', filter.projectId)
    if (filter.customerId) p.set('customerId', filter.customerId)
    return request<{ lines: OpenLine[] }>(`/rapports/open-lines?${p.toString()}`)
  },

  listDocuments: (
    status: 'alle' | 'entwurf' | 'offen' | 'abgeschlossen' | 'gesendet' | 'storniert' = 'entwurf'
  ) => request<{ documents: DocumentSummary[] }>(`/documents?status=${status}`),

  getDocument: (id: string) =>
    request<{ document: DocumentDetail; lines: DocumentLine[]; totals: DocumentTotals }>(
      `/documents/${id}`
    ),

  /**
   * Beleg direkt anlegen, ohne Rapport – der Weg der Express-Rechnung.
   *
   * Der Beleg entsteht leer; die Positionen kommen danach über
   * addDocumentLine. Dieselbe Kette wie beim Rapportweg, nur ohne den ersten
   * Schritt.
   */
  createExpressDocument: (body: ExpressKopf, idempotencyKey: string) =>
    request<{ document: DocumentDetail; lineCount: number }>('/documents', {
      method: 'POST',
      body,
      idempotencyKey
    }),

  /**
   * Die Arbeitsliste des Büros.
   *
   * Ein Aufruf, weil alles darauf zusammen gelesen wird und ein halb gefülltes
   * Dashboard schlimmer ist als ein eine Sekunde später vollständiges.
   */
  dashboard: () => request<DashboardDaten>('/dashboard'),

  // -------------------------------------------------------------------------
  // Offerten
  // -------------------------------------------------------------------------

  quoteList: (filter: OffertFilter = 'offen', suche = '', datum?: Datumsfilter) => {
    const p = new URLSearchParams({ filter, ...datumsteile(datum) })
    if (suche.trim()) p.set('suche', suche.trim())
    return request<OffertenListe>(`/quotes?${p.toString()}`)
  },

  /** Was der Kunde gesagt hat. null nimmt eine Antwort zurück. */
  setQuoteOutcome: (
    id: string,
    outcome: 'angenommen' | 'abgelehnt' | null,
    note: string | null,
    idempotencyKey: string
  ) =>
    request<{ id: string; outcome: string | null }>(`/quotes/${id}/outcome`, {
      method: 'PUT',
      body: { outcome, note },
      idempotencyKey
    }),

  /**
   * Erzeugt sofort einen Rechnungsentwurf. Die Offerte bleibt, wie sie ist.
   *
   * Der Leistungszeitraum ist Pflicht: er steht auf der Rechnung, und nach ihm
   * richtet sich die Mehrwertsteuerperiode. Die Offerte kann ihn nicht liefern
   * – sie beschreibt Arbeit, die noch nicht stattgefunden hat.
   */
  quoteToInvoice: (
    id: string,
    alsAngenommenMarkieren: boolean,
    leistung: { von: string; bis: string },
    idempotencyKey: string
  ) =>
    request<{
      document: { id: string; number: string | null; status: string; totalRp: number }
      lineCount: number
      offertnummer: string
    }>(`/quotes/${id}/to-invoice`, {
      method: 'POST',
      body: {
        alsAngenommenMarkieren,
        serviceFrom: leistung.von,
        serviceTo: leistung.bis
      },
      idempotencyKey
    }),

  /**
   * Aus der Offerte eine Baustelle machen.
   *
   * Der Weg für alles, was länger dauert als einen Tag: Offerte → Baustelle →
   * Rapporte → Rechnung. Positionen kommen bewusst nicht mit – ein Angebot ist
   * keine geleistete Arbeit.
   */
  quoteToProject: (id: string, name: string | null, idempotencyKey: string) =>
    request<{ projektId: string; name: string; offertnummer: string | null }>(
      `/quotes/${id}/to-project`,
      { method: 'POST', body: name ? { name } : {}, idempotencyKey }
    ),

  cancelQuote: (id: string, reason: string, idempotencyKey: string) =>
    request<{ id: string }>(`/quotes/${id}/cancel`, {
      method: 'POST',
      body: { reason },
      idempotencyKey
    }),

  lastQuoteFor: (customerId: string) =>
    request<LetzteRechnung>(`/quotes/last-for-customer?customerId=${customerId}`),

  // -------------------------------------------------------------------------
  // Zahlungsbereich
  // -------------------------------------------------------------------------

  paymentList: (filter: ZahlungsFilter = 'offen', suche = '', zeitraum: Zeitraum = 'monat') => {
    const p = new URLSearchParams({ filter, zeitraum })
    if (suche.trim()) p.set('suche', suche.trim())
    return request<ZahlungsListe>(`/payments?${p.toString()}`)
  },

  paymentHistory: (
    zeige: 'gueltig' | 'storniert' | 'alle' = 'gueltig',
    suche = '',
    zeitraum: Zeitraum = 'alles'
  ) => {
    const p = new URLSearchParams({ zeige, zeitraum })
    if (suche.trim()) p.set('suche', suche.trim())
    return request<ZahlungsHistorie>(`/payments/history?${p.toString()}`)
  },

  // -------------------------------------------------------------------------
  // Vorlagen und die beiden anderen Wege zu einem Entwurf
  // -------------------------------------------------------------------------

  listTemplates: (type: 'rechnung' | 'offerte' | 'alle' = 'rechnung') =>
    request<{ templates: TemplateSummary[] }>(`/templates?type=${type}`),

  getTemplate: (id: string) =>
    request<{ template: TemplateDetail; lines: TemplateLine[] }>(`/templates/${id}`),

  createTemplate: (body: NewTemplate, idempotencyKey: string) =>
    request<{ template: TemplateDetail; lineCount: number }>('/templates', {
      method: 'POST',
      body,
      idempotencyKey
    }),

  removeTemplate: (id: string, idempotencyKey: string) =>
    request<{ id: string }>(`/templates/${id}`, { method: 'DELETE', idempotencyKey }),

  /* ----- Kita · Betreuungsverhaeltnisse und Monatsrechnungen -----
     Alle Pfade pruefen die Branche am Server. `betreuungVerfuegbar` ist eine
     Auskunft fuer die Navigation und schaltet nichts frei. */
  betreuungVerfuegbar: () => request<{ verfuegbar: boolean }>('/betreuung/verfuegbar'),

  /* ───────────────────────────────────────────────────────────────────────
     Die Kita-Gemeinschaft

     Organisatorisches, und nur das: gemeinsamer Name, Logo, Adresse und die
     Frage, wer dazugehört. Kein Aufruf hier liest ein Kind, eine Rechnung
     oder ein Konto eines anderen Betriebs – es gibt am Server keine Route,
     die das könnte.
     ─────────────────────────────────────────────────────────────────────── */

  /** Die Gemeinschaft dieses Betriebs – oder `{ dach: null }`. */
  dach: () => request<Dachsicht>('/dach'),

  dachAnlegen: (body: Dacheingabe, idempotencyKey: string) =>
    request<{ dach: Dachorganisation }>('/dach', { method: 'POST', body, idempotencyKey }),

  dachAendern: (body: Dacheingabe, idempotencyKey: string) =>
    request<{ dach: Dachorganisation }>('/dach', { method: 'PATCH', body, idempotencyKey }),

  /** Das Logo des eigenen Betriebs als gemeinsames Kita-Logo übernehmen. */
  dachLogoUebernehmen: (idempotencyKey: string) =>
    request<{ ok: true }>('/dach/logo', { method: 'POST', body: {}, idempotencyKey }),

  dachLogoEntfernen: (idempotencyKey: string) =>
    request<{ ok: true }>('/dach/logo', { method: 'DELETE', idempotencyKey }),

  /** Die Adresse des gemeinsamen Logos – für das <img> im Dachbereich. */
  dachLogoUrl: (stand: string) => `${config.baseUrl}/dach/logo?v=${encodeURIComponent(stand)}`,

  dachEinladen: (body: { name: string; email: string }, idempotencyKey: string) =>
    request<{ einladung: Dacheinladung }>('/dach/einladungen', {
      method: 'POST',
      body,
      idempotencyKey
    }),

  dachEinladungZurueckziehen: (id: string, idempotencyKey: string) =>
    request<{ ok: true }>(`/dach/einladungen/${id}`, { method: 'DELETE', idempotencyKey }),

  /** Was auf der Einladungsseite steht. Ohne Anmeldung abrufbar. */
  dachEinladungAnsehen: (token: string) =>
    request<{ einladung: Einladungsansicht }>(
      `/dach/einladung?token=${encodeURIComponent(token)}`
    ),

  /** Das Zeichen der Kita auf der Einladungsseite. Ohne Anmeldung abrufbar. */
  dachEinladungLogoUrl: (token: string) =>
    `${config.baseUrl}/dach/einladung/logo?token=${encodeURIComponent(token)}`,

  /** Mit dem bestehenden Betrieb beitreten. */
  dachBeitreten: (token: string, idempotencyKey: string) =>
    request<{ dach: Dachorganisation }>('/dach/beitritt', {
      method: 'POST',
      body: { token },
      idempotencyKey
    }),

  dachVerwalterHinzufuegen: (userId: string, idempotencyKey: string) =>
    request<{ ok: true }>('/dach/verwalter', { method: 'POST', body: { userId }, idempotencyKey }),

  dachVerwalterEntfernen: (userId: string, idempotencyKey: string) =>
    request<{ ok: true }>(`/dach/verwalter/${userId}`, { method: 'DELETE', idempotencyKey }),

  dachAustritt: (tenantId: string | undefined, idempotencyKey: string) =>
    request<{ hinweis: string }>('/dach/austritt', {
      method: 'POST',
      body: tenantId ? { tenantId } : {},
      idempotencyKey
    }),

  betreuungVerhaeltnisse: () =>
    request<{ verhaeltnisse: Betreuungsverhaeltnis[] }>('/betreuung/verhaeltnisse'),

  betreuungAnlegen: (body: Betreuungseingabe, idempotencyKey: string) =>
    request<{ id: string }>('/betreuung/verhaeltnisse', {
      method: 'POST',
      body,
      idempotencyKey
    }),

  betreuungAendern: (id: string, body: Betreuungseingabe, idempotencyKey: string) =>
    request<{ id: string }>(`/betreuung/verhaeltnisse/${id}`, {
      method: 'PATCH',
      body,
      idempotencyKey
    }),

  betreuungStilllegen: (id: string, idempotencyKey: string) =>
    request<{ ok: boolean }>(`/betreuung/verhaeltnisse/${id}`, {
      method: 'DELETE',
      idempotencyKey
    }),

  betreuungMonat: (jahr: number, monat: number) =>
    request<Betreuungsmonat>(`/betreuung/monat?jahr=${jahr}&monat=${monat}`),

  /**
   * Eine bestehende Monatsrechnung ersetzen – Storno und Ersatz in einem
   * Vorgang am Server. Die Bestätigung holt der Bildschirm vorher ein.
   */
  betreuungErsetzen: (
    verhaeltnisId: string,
    jahr: number,
    monat: number,
    idempotencyKey: string
  ) =>
    request<{
      documentId: string
      positionen: number
      nettoRp: number
      ersetzt: { documentId: string; nummer: string | null; weg: 'storniert' | 'verworfen' }
    }>('/betreuung/monat/ersetzen', {
      method: 'POST',
      body: { verhaeltnisId, jahr, monat },
      idempotencyKey
    }),

  betreuungEntwurf: (verhaeltnisId: string, jahr: number, monat: number, idempotencyKey: string) =>
    request<{ documentId: string; positionen: number; nettoRp: number }>(
      '/betreuung/monat/entwurf',
      { method: 'POST', body: { verhaeltnisId, jahr, monat }, idempotencyKey }
    ),

  /**
   * Mehrere Entwürfe eines Monats abschliessen.
   *
   * EINE Aktionskennung für den ganzen Lauf: Der Server bildet daraus je
   * Beleg eine eigene, feste Kennung. Ein zweites Absenden desselben Laufs
   * trifft damit auf dieselben Kennungen und schliesst nichts doppelt ab.
   */
  betreuungMonatAbschluss: (
    jahr: number,
    monat: number,
    documentIds: string[],
    idempotencyKey: string
  ) =>
    request<BetreuungAbschluss>('/betreuung/monat/abschluss', {
      method: 'POST',
      body: { jahr, monat, documentIds },
      idempotencyKey
    }),

  /**
   * Eine Datei als Bytes holen – für «Speichern unter …».
   *
   * `pfad` ist derselbe Pfad, den die Ansicht auch als Verweis benutzt
   * (`/documents/<id>/pdf`). Der gewöhnliche Download braucht diese
   * Funktion nicht: Dort lädt der Browser selbst, und nichts geht durch den
   * Speicher der Seite. Gebraucht wird sie nur, wo der Betrieb den
   * Speicherort im nativen Dialog wählt – dort will der Dialog die Bytes.
   */
  dateiLaden: (pfad: string) =>
    request<Blob>(pfad, { roh: true, timeoutMs: TIMEOUT_LONG_MS }),

  /**
   * Die ZIP eines Betreuungsmonats als Bytes – dieselbe Adresse wie
   * `betreuungMonatPdfs`, nur geladen statt verlinkt.
   */
  betreuungMonatPdfsLaden: (jahr: number, monat: number) =>
    request<Blob>(`/betreuung/monat/pdfs?jahr=${jahr}&monat=${monat}`, {
      roh: true,
      timeoutMs: TIMEOUT_LONG_MS
    }),

  /** Alle abgeschlossenen Rechnungen eines Betreuungsmonats als ZIP. */
  betreuungMonatPdfs: (jahr: number, monat: number) =>
    `${config.baseUrl}/betreuung/monat/pdfs?jahr=${jahr}&monat=${monat}`,

  /** Liest nur. Der Entwurf entsteht erst, wenn der Mensch speichert. */
  lastInvoiceFor: (customerId: string, type: 'rechnung' | 'offerte' = 'rechnung') =>
    request<LetzteRechnung>(`/documents/last-for-customer?customerId=${customerId}&type=${type}`),

  /** Schreibt: legt sofort einen neuen Entwurf an. Das Original bleibt. */
  duplicateDocument: (id: string, idempotencyKey: string) =>
    request<{ document: DocumentDetail; lineCount: number; quelle: string }>(
      `/documents/${id}/duplicate`,
      { method: 'POST', body: {}, idempotencyKey }
    ),

  /** Nächster Schritt – geht auch bei abgeschlossenen Belegen. */
  setNextAction: (
    id: string,
    body: { label: string | null; date?: string | null; note?: string | null; done?: boolean },
    idempotencyKey: string
  ) =>
    request<{ id: string; version: number }>(`/documents/${id}/next-action`, {
      method: 'PUT',
      body,
      idempotencyKey
    }),

  createDocument: (
    body: { id?: string; customerId: string; projectId?: string | null; rapportLineIds: string[] },
    idempotencyKey: string
  ) =>
    request<{ document: DocumentDetail; lineCount: number }>('/documents/from-rapports', {
      method: 'POST',
      body,
      idempotencyKey
    }),

  addRapportLines: (id: string, rapportLineIds: string[], idempotencyKey: string) =>
    request<{ lineCount: number } & DocumentTotals>(`/documents/${id}/from-rapports`, {
      method: 'POST',
      body: { rapportLineIds },
      idempotencyKey
    }),

  /*
    Die drei Positionswege geben die neue Version des Belegs zurück und nehmen
    wahlweise `basisVersion` an. Fehlt sie, wird nur erhöht; ist sie veraltet,
    antwortet der Server mit 409 statt eine fremde Änderung zu überschreiben.
  */
  addDocumentLine: (
    id: string,
    body: NewDocumentLine & { basisVersion?: number },
    idempotencyKey: string
  ) =>
    request<{ lineId: string; version: number } & DocumentTotals>(`/documents/${id}/lines`, {
      method: 'POST',
      body,
      idempotencyKey
    }),

  updateDocumentLine: (
    id: string,
    lineId: string,
    body: Partial<NewDocumentLine> & { basisVersion?: number },
    idempotencyKey: string
  ) =>
    request<{ lineId: string; version: number } & DocumentTotals>(
      `/documents/${id}/lines/${lineId}`,
      {
        method: 'PATCH',
        body,
        idempotencyKey
      }
    ),

  removeDocumentLine: (
    id: string,
    lineId: string,
    idempotencyKey: string,
    basisVersion?: number
  ) =>
    request<{ lineId: string; version: number; freigegeben: string | null } & DocumentTotals>(
      basisVersion === undefined
        ? `/documents/${id}/lines/${lineId}`
        : `/documents/${id}/lines/${lineId}?basisVersion=${basisVersion}`,
      { method: 'DELETE', idempotencyKey }
    ),

  /**
   * Kopfdaten eines Entwurfs ändern.
   *
   * Die Felder decken sich mit ExpressKopf – derselbe Beleg, nur später
   * bearbeitet. Der Gesamtrabatt kommt dazu: er lässt sich beim Anlegen noch
   * nicht sinnvoll setzen, weil es dann noch keine Positionen gibt, auf die
   * er wirken könnte.
   */
  updateDocument: (
    id: string,
    body: Partial<Omit<ExpressKopf, 'id' | 'type' | 'customerId'>> & {
      discountPercent?: number
      /* Der Konfliktschutz an der ersten Schreiboperation eines Durchgangs. */
      basisVersion?: number
    },
    idempotencyKey: string
  ) =>
    /* `version` ist die vom Server bestätigte neue Version – die Express-Maske
       sichert in einem Durchgang zweimal und braucht sie für `basisVersion`. */
    request<{ id: string; version: number }>(`/documents/${id}`, {
      method: 'PATCH',
      body,
      idempotencyKey
    }),

  discardDocument: (id: string, idempotencyKey: string) =>
    request<{ id: string; freigegebeneZeilen: number; message: string }>(`/documents/${id}`, {
      method: 'DELETE',
      idempotencyKey
    }),

  cancelDocument: (id: string, reason: string, idempotencyKey: string) =>
    request<{ id: string; freigegebeneZeilen: number; message: string }>(
      `/documents/${id}/cancel`,
      { method: 'POST', body: { reason }, idempotencyKey }
    ),

  /**
   * Mehrere Belege auf einmal – abschliessen, verwerfen, stornieren.
   *
   * Dieselbe lange Zeitgrenze wie beim einzelnen Abschluss, und aus demselben
   * Grund: Der Server druckt je Beleg ein PDF. Er nimmt dafür höchstens zehn
   * Belege an; die Oberfläche hakt nicht mehr an, und der Server weist mehr
   * mit einer Zahl im Text ab.
   */
  belegSammelaktion: (aktion: Sammelaktion, ids: string[], grund: string, idempotencyKey: string) =>
    request<SammelErgebnis>('/documents/bulk', {
      method: 'POST',
      body: { aktion, ids, grund },
      idempotencyKey,
      timeoutMs: TIMEOUT_LONG_MS
    }),

  /**
   * Zwei Schritte, absichtlich: ohne `confirm` antwortet der Server nur, was es
   * schon Ähnliches gibt. Erst der zweite Aufruf legt an.
   */
  lineToCatalog: (
    id: string,
    lineId: string,
    body: { code?: string; confirm?: boolean },
    idempotencyKey: string
  ) =>
    request<ToCatalogAnswer>(`/documents/${id}/lines/${lineId}/to-catalog`, {
      method: 'POST',
      body,
      idempotencyKey
    }),

  // -------------------------------------------------------------------------
  // Abschluss und Versand (Block 6)
  //
  // Alle drei sind Klasse B: sie erzeugen etwas, das sich nicht zurücknehmen
  // lässt. Deshalb längere Zeitgrenze – ein Druckvorgang dauert ein bis zwei
  // Sekunden, und ein Abbruch nach 15 Sekunden hinterliesse eine Rechnung mit
  // Nummer und ohne PDF, obwohl der Server noch am Arbeiten war.
  // -------------------------------------------------------------------------

  finalizeDocument: (id: string, idempotencyKey: string) =>
    request<FinalizeAntwort>(`/documents/${id}/finalize`, {
      method: 'POST',
      body: {},
      idempotencyKey,
      timeoutMs: TIMEOUT_LONG_MS
    }),

  /**
   * Was an diesem Beleg möglich ist – Adressen, Sperren, letzte Versande.
   *
   * Eigener Aufruf und nicht Teil des Belegdetails: er hängt an Dingen, die
   * sich unabhängig vom Beleg ändern (der SMTP-Zugang, die eigene Adresse),
   * und das Belegdetail wird an vielen Stellen geladen, an denen niemand
   * versenden will.
   */
  versandwege: (id: string) => request<Versandwege>(`/documents/${id}/versandwege`),

  /**
   * Von Hand als versendet vermerken – ohne dass Raporto etwas sendet.
   *
   * Für den Betrieb, der das PDF ausdruckt und in die Post legt. Ohne diesen
   * Weg bliebe seine Rechnung für Raporto ewig «nicht versendet» und würde
   * nie überfällig.
   */
  alsVersendetVermerken: (id: string, idempotencyKey: string) =>
    request<{ id: string; vermerktAm: string; vermerktVon: string | null }>(
      `/documents/${id}/als-versendet`,
      { method: 'POST', body: {}, idempotencyKey }
    ),

  vermerkEntfernen: (id: string, idempotencyKey: string) =>
    request<{ id: string; vermerktAm: null }>(`/documents/${id}/als-versendet`, {
      method: 'DELETE',
      idempotencyKey
    }),

  /**
   * Einen unklar gebliebenen Versand klären.
   *
   * Ein unklarer Versand sperrt den Beleg: Die Mail kann beim Kunden liegen,
   * also darf das Blatt nicht mehr geändert werden – und als versendet gilt
   * es trotzdem nicht. Aus diesem Zustand führt nur, wer nachgesehen hat.
   *
   *   `angekommen`       → gilt als versendet, Sperre bleibt mit Grund
   *   `nicht-angekommen` → Versuch geschlossen, Beleg wieder bearbeitbar
   *
   * Es wird dabei nichts gesendet. Der erneute Versand ist ein eigener,
   * ausdrücklicher Schritt danach.
   */
  versandKlaeren: (
    id: string,
    ergebnis: 'angekommen' | 'nicht-angekommen',
    idempotencyKey: string,
    bemerkung?: string
  ) =>
    request<{ id: string; ergebnis: string; geschlossen: number }>(
      `/documents/${id}/versand-klaeren`,
      { method: 'POST', body: { ergebnis, bemerkung }, idempotencyKey }
    ),

  /**
   * Den ganzen Entwurf in EINEM Zug ersetzen.
   *
   * Die Grundlage des automatischen Speicherns: eine Anfrage, eine
   * Transaktion. Der frühere Weg löschte jede Zeile einzeln und legte sie
   * einzeln neu an – bei zwölf Positionen fünfundzwanzig Aufrufe, und ein
   * Abbruch in der Mitte hinterliess einen halben Beleg.
   *
   * `basisVersion` ist der Konfliktschutz: Wer nicht auf dem aktuellen Stand
   * ist, bekommt 409 statt stillschweigend fremde Arbeit zu überschreiben.
   */
  entwurfErsetzen: (
    id: string,
    rumpf: {
      basisVersion?: number
      kopf?: Record<string, unknown>
      zeilen: NewDocumentLine[]
    },
    idempotencyKey: string
  ) =>
    request<{
      id: string
      version: number
      zeilen: number
      subtotalRp: number
      vatTotalRp: number
      totalRp: number
      rundungRp: number
      changeSeq: number
    }>(`/documents/${id}/entwurf`, { method: 'PUT', body: rumpf, idempotencyKey }),

  /**
   * Der Vorschlag für die E-Mail – Empfänger, Betreff, Text.
   *
   * Reine Auskunft. Sie füllt den Editor, der vor jedem Versand aufgeht.
   */
  mailvorschlag: (id: string, weg: 'benutzer' | 'firma' | 'kunde' = 'kunde') =>
    request<{ weg: string; empfaenger: string; betreff: string; text: string }>(
      `/documents/${id}/mailvorschlag?weg=${weg}`
    ),

  /**
   * Versenden.
   *
   * `betreff` und `text` kommen aus dem Editor. Fehlen sie, gilt die Vorlage
   * des Servers – so bleibt der Sammelversand unverändert lauffähig.
   */
  versenden: (
    id: string,
    weg: 'benutzer' | 'firma' | 'kunde',
    idempotencyKey: string,
    nachricht?: { betreff: string; text: string }
  ) =>
    request<{ weg: string; empfaenger: string; nummer: string }>(`/documents/${id}/versand`, {
      method: 'POST',
      body: nachricht ? { weg, betreff: nachricht.betreff, text: nachricht.text } : { weg },
      idempotencyKey,
      timeoutMs: TIMEOUT_LONG_MS
    }),

  /**
   * Die Einstellungen frisch vom Server.
   *
   * Der Bildschirm liest sonst aus dem Zwischenspeicher, den der Abgleich
   * füllt. Nach dem Speichern des SMTP-Zugangs wäre der einen Abgleich lang
   * veraltet – und genau dann schaut jemand hin, ob es geklappt hat.
   */
  settingsLesen: () => request<{ settings: Record<string, unknown> | null }>('/settings'),

  smtpSpeichern: (eingabe: SmtpEingabe, idempotencyKey: string) =>
    request<{ gespeichert: true; passwortGesetzt: boolean }>('/settings/smtp', {
      method: 'PATCH',
      body: eingabe,
      idempotencyKey
    }),

  smtpTesten: (
    eingabe: { testmail: boolean; ziel: 'benutzer' | 'firma' },
    idempotencyKey: string
  ) =>
    request<{ geprueft: true; testmailGesendet: boolean }>('/settings/smtp/test', {
      method: 'POST',
      body: eingabe,
      idempotencyKey,
      timeoutMs: TIMEOUT_LONG_MS
    }),

  regeneratePdf: (id: string, idempotencyKey: string) =>
    request<FinalizeAntwort>(`/documents/${id}/pdf`, {
      method: 'POST',
      body: {},
      idempotencyKey,
      timeoutMs: TIMEOUT_LONG_MS
    }),

  sendDocument: (
    id: string,
    body: { to: string; subject: string; text: string },
    idempotencyKey: string
  ) =>
    request<{ send: SendAttempt | null }>(`/documents/${id}/send`, {
      method: 'POST',
      body,
      idempotencyKey,
      timeoutMs: TIMEOUT_LONG_MS
    }),

  listSends: (id: string) =>
    request<{ sends: SendAttempt[]; providerDeliversForReal: boolean; providerName: string }>(
      `/documents/${id}/sends`
    ),

  // -------------------------------------------------------------------------
  // Zahlungen, Arbeitsliste, Export (Block 7)
  // -------------------------------------------------------------------------

  listPayments: (documentId: string) =>
    request<{ payments: Payment[] } & Zahlungsstand>(`/documents/${documentId}/payments`),

  recordPayment: (
    documentId: string,
    body: { amountRp: number; paidAt: string; note?: string },
    idempotencyKey: string
  ) =>
    request<{ paymentId: string } & Zahlungsstand>(`/documents/${documentId}/payments`, {
      method: 'POST',
      body,
      idempotencyKey
    }),

  /**
   * Zahlungseingänge für mehrere Rechnungen auf einmal.
   *
   * Ein Datum für alle, je Rechnung ein Betrag. Derselbe Weg wie beim
   * einzelnen Erfassen – der Server läuft je Rechnung durch dieselbe
   * Funktion.
   */
  zahlungenSammeln: (
    body: { paidAt: string; note?: string; posten: Array<{ id: string; amountRp: number }> },
    idempotencyKey: string
  ) =>
    request<ZahlungsSammelErgebnis>('/payments/bulk', {
      method: 'POST',
      body,
      idempotencyKey
    }),

  /**
   * Eine erfasste Zahlung berichtigen – in einem Vorgang.
   *
   * Der Server storniert die alte Zeile und schreibt die berichtigte; beide
   * Schritte gelten zusammen oder gar nicht. `ueberzahlungOk` ist die Antwort
   * auf die Rückfrage, die kommt, wenn der neue Betrag mehr ist als offen.
   */
  correctPayment: (
    paymentId: string,
    body: {
      amountRp: number
      paidAt: string
      note?: string
      grund: string
      ueberzahlungOk?: boolean
    },
    idempotencyKey: string
  ) =>
    request<{ paymentId: string; ersetztePaymentId: string; nummer: string | null } & Zahlungsstand>(
      `/payments/${paymentId}/correct`,
      { method: 'POST', body, idempotencyKey }
    ),

  voidPayment: (paymentId: string, reason: string, idempotencyKey: string) =>
    request<{ paymentId: string } & Zahlungsstand>(`/payments/${paymentId}/void`, {
      method: 'POST',
      body: { reason },
      idempotencyKey
    }),

  openWork: () =>
    request<{
      items: OffenerPunkt[]
      zusammenfassung: { gesamt: number; probleme: number; ueberfaellig: number; offenRp: number }
    }>('/open-work'),

  exportPreview: () =>
    request<{
      rechnungen: number
      zahlungen: number
      kunden: number
      pdfs: number
      ohnePdf: Array<{ number: string; status: string }>
      enthalten: string[]
      nichtEnthalten: string[]
    }>('/exports/preview'),

  // -------------------------------------------------------------------------
  // Büro-Listen
  // -------------------------------------------------------------------------

  /*
    Ein Bauteil für fünf Listen.

    Alle Büro-Listen antworten gleich – Posten, Zähler, Summe – und lassen
    sich mit denselben zwei Angaben abfragen. Fünf fast gleiche Funktionen
    wären fünf Stellen, an denen jemand das Suchfeld vergisst zu kodieren.
  */
  officeInvoices: (filter: RechnungsFilter = 'offen', suche = '', datum?: Datumsfilter) =>
    request<BueroListe<BueroRechnung, RechnungsFilter>>(
      bueroWeg('/office/invoices', filter, suche, datumsteile(datum))
    ),

  officeCustomers: (filter: KundenFilter = 'aktiv', suche = '') =>
    request<BueroListe<BueroKunde, KundenFilter>>(bueroWeg('/office/customers', filter, suche)),

  officeCustomer: (id: string) => request<KundenBlatt>(`/office/customers/${id}`),

  officeProjects: (filter: ProjektFilter = 'offen', suche = '', customerId?: string) =>
    request<BueroListe<BueroProjekt, ProjektFilter>>(
      bueroWeg('/office/projects', filter, suche, customerId ? { customerId } : undefined)
    ),

  officeProject: (id: string) => request<ProjektBlatt>(`/office/projects/${id}`),

  /**
   * Ein Projekt abschliessen oder wieder öffnen.
   *
   * Derselbe Weg wie jede Projektänderung (`PATCH /projects/:id`) – mit den
   * Stammdaten, wie das Blatt sie zeigt, und mit `expectedVersion`: Hat
   * jemand das Projekt inzwischen geändert, lehnt der Server ab, statt es
   * mit dem alten Stand zu überschreiben. Rapporte, Belege und Zuweisungen
   * berührt der Status nicht.
   */
  projektStatusSetzen: (
    p: ProjektBlatt['projekt'],
    status: 'offen' | 'abgeschlossen',
    idempotencyKey: string
  ) =>
    request<{ project: { version: number; status: string } }>(`/projects/${p.id}`, {
      method: 'PATCH',
      body: {
        customerId: p.customerId,
        name: p.name,
        street: p.street,
        zip: p.zip,
        city: p.city,
        notes: p.notes,
        status,
        expectedVersion: p.version
      },
      idempotencyKey
    }),

  /**
   * Wer diesem Projekt zugewiesen ist.
   *
   * Kommt aus `GET /projects/:id`, nicht aus dem Büro-Blatt: die Zuweisung ist
   * eine Stammdatenfrage und steht dort, wo sie auch gesetzt wird.
   */
  projektMitglieder: (id: string) =>
    request<{ project: Record<string, unknown>; members: Array<{ userId: string }> }>(
      `/projects/${id}`
    ),

  /** Die Zuweisung setzen – ersetzt die bisherige Liste vollständig. */
  projektMitgliederSetzen: (id: string, userIds: string[], idempotencyKey: string) =>
    request<{ ok?: boolean }>(`/projects/${id}/members`, {
      method: 'PUT',
      body: { userIds },
      idempotencyKey
    }),

  officeCatalog: (filter: KatalogFilter = 'alle', suche = '') =>
    request<BueroListe<BueroLeistung, KatalogFilter>>(bueroWeg('/office/catalog', filter, suche)),

  /*
    Handelsregister-Suche.

    Der Weg geht über unseren Server und nicht direkt an den Zentralen
    Firmenindex. Das ist kein Umweg, sondern der Punkt: das Zugangswort liegt
    auf dem Server, und dort bleibt es. Ein Aufruf von hier aus bräuchte es im
    Browser – und damit auf jedem Rechner, der die Anwendung öffnet.
  */
  /**
   * Das eigene Profil – Name und Telefonnummer der angemeldeten Person.
   *
   * Getrennt von /settings, und das ist keine Formsache: /settings gehört der
   * Firma. Wer dort seine Handynummer einträgt, trägt sie auf jede Rechnung
   * ein, die der Betrieb je verschickt.
   */
  profilSetzen: (werte: { name?: string; phone?: string | null }, idempotencyKey: string) =>
    request<{ profil: { name: string; phone: string | null } }>('/security/profil', {
      method: 'PATCH',
      body: werte,
      idempotencyKey
    }),

  /**
   * Name und Nummer einer anderen Person im Betrieb setzen.
   *
   * Getrennt von profilSetzen, weil es etwas anderes ist: dort pflegt jemand
   * seine eigenen Angaben, hier pflegt die Betriebsleitung den Personenstamm.
   * Die Mailadresse ist in beiden Fällen unantastbar – an ihr hängen Anmeldung
   * und zweiter Faktor.
   */
  teamAngabenSetzen: (
    userId: string,
    werte: { name?: string; phone?: string | null },
    idempotencyKey: string
  ) =>
    request<{ user: TenantUser | null }>(`/users/${userId}`, {
      method: 'PATCH',
      body: werte,
      idempotencyKey
    }),

  /* ---------------------------------------------------------------- Bestellung */
  /**
   * Was die Registrierung kann, bevor jemand etwas tippt.
   *
   * Insbesondere, ob die Firmensuche zur Verfügung steht. Ein Suchfeld, das
   * nichts findet, weil im Hintergrund ein Zugang fehlt, ist schlimmer als
   * keines – der Besucher hält die Software für kaputt, bevor er sie hat.
   */
  registrierungMoeglichkeiten: () =>
    request<{
      handelsregister: boolean
      testphaseTage: number
      branchen: Array<{ id: string; startpaket: boolean }>
      /* Ob ein Einladungscode nötig ist – nie, welcher. */
      codeNoetig: boolean
      /*
        Ob die Einrichtung über die Zahlung läuft, und ob das der Testmodus
        ist. Zwei Wahrheitswerte, keine Schlüssel.
      */
      zahlung: {
        verfuegbar: boolean
        sandbox: boolean
        /* Der oeffentliche Stripe-Schluessel – fuer den Browser bestimmt und
           dafuer gebaut. Der geheime kommt hier nie an. */
        publicKey: string
      }
      /*
        Die Preise, wie sie im Stripe-Konto stehen – nicht wie sie in
        `pakete.ts` stehen. Leer, wenn die Zahlung nicht eingerichtet ist oder
        Stripe gerade nicht antwortet; dann zeigt das Formular keine Zahl
        statt einer erfundenen.
      */
      preise: Array<{ paket: string; intervall: string; betragRp: number | null }>
    }>('/registrierung/moeglichkeiten'),

  /** Firmensuche im Handelsregister – ohne Sitzung, mit dem Zugang der Plattform. */
  registrierungFirma: (name: string) =>
    request<{ treffer: ZefixTreffer[] }>(`/registrierung/firma?name=${encodeURIComponent(name)}`),

  /**
   * Bestellen.
   *
   * Der Schlüssel wird mitgeschickt, weil jede schreibende Aktion einen trägt –
   * die Regel gilt ohne Ausnahme, sonst ist sie keine. Er schützt hier aber
   * nicht allein: gegen den doppelten Klick steht die Eindeutigkeit der
   * Mailadresse. Der zweite Versuch bekommt 409 und nicht einen zweiten
   * Betrieb.
   */
  registrieren: (bestellung: Bestellung, idempotencyKey: string) =>
    request<{ session: unknown; testphaseBis: string; katalogzeilen: number }>('/registrierung', {
      method: 'POST',
      body: bestellung,
      idempotencyKey
    }),

  /**
   * «Weiter zur Zahlung».
   *
   * Derselbe Körper wie bei `registrieren`, plus das Intervall – und ein ganz
   * anderes Ergebnis: KEIN Betrieb, keine Sitzung, sondern der Schlüssel für
   * eine eingebettete Stripe-Kasse. Er gilt für genau diese eine Sitzung und
   * schaltet nichts frei.
   *
   * Eine Preiskennung wird ausdrücklich NICHT mitgeschickt. Welcher Preis
   * gilt, entscheidet der Server aus Paket und Intervall gegen das
   * Stripe-Konto; ein Feld dafür gäbe es hier nur, damit jemand es fälschen
   * kann.
   */
  registrierungCheckout: (
    bestellung: Bestellung & { intervall: 'monat' | 'jahr' },
    idempotencyKey: string
  ) =>
    request<{ clientSecret: string }>('/registrierung/checkout', {
      method: 'POST',
      body: bestellung,
      idempotencyKey
    }),

  /**
   * «Einrichtung wird abgeschlossen» – der Stand.
   *
   * Sie fragt nach, bis der Webhook da war. Der Schlüssel aus der
   * Rückkehradresse geht mit; er wird genau einmal eingelöst und ergibt dann
   * die Sitzung. Ohne ihn – oder ein zweites Mal – kommt die Auskunft, aber
   * keine Anmeldung.
   */
  einrichtungsstand: (sitzung: string, schluessel: string) =>
    request<{
      fertig: boolean
      status: 'offen' | 'erledigt' | 'abgelaufen' | 'unbekannt'
      session: unknown | null
      /* Die Adresse, mit der sich der neue Inhaber anmeldet. */
      anmeldeEmail: string | null
    }>(
      `/registrierung/stand?sitzung=${encodeURIComponent(sitzung)}` +
        (schluessel ? `&schluessel=${encodeURIComponent(schluessel)}` : '')
    ),

  /**
   * Den Sperrbildschirm öffnen.
   *
   * Kein zweites Anmelden: das erzeugte eine neue Sitzung, und der halbfertige
   * Beleg auf dem Bildschirm wäre weg. Hier wird nur das Passwort geprüft.
   */
  entsperren: (passwort: string, idempotencyKey: string) =>
    request<{ entsperrt: true }>('/security/entsperren', {
      method: 'POST',
      body: { passwort },
      idempotencyKey
    }),

  /* ------------------------------------------------------ Offene Anmeldungen */
  /**
   * Wo bin ich überall noch angemeldet?
   *
   * Die Frage stellt jemand, dessen Laptop im Zug liegen geblieben ist. Die
   * Antwort enthält nie den Sitzungsschlüssel – nur, woran man ein Gerät
   * wiedererkennt.
   */
  anmeldungen: () =>
    request<{
      anmeldungen: Array<{
        id: string
        createdAt: string
        lastSeenAt: string
        userAgent: string
        diese: boolean
      }>
    }>('/security/anmeldungen'),

  /** Alle anderen Anmeldungen beenden. Die eigene bleibt. */
  anmeldungenBeenden: (idempotencyKey: string) =>
    request<{ beendet: number }>('/security/anmeldungen/beenden', {
      method: 'POST',
      idempotencyKey
    }),

  /** Die Anmeldungen einer anderen Person im Betrieb beenden. */
  fremdeAnmeldungenBeenden: (userId: string, idempotencyKey: string) =>
    request<{ beendet: number }>(`/security/anmeldungen/${userId}/beenden`, {
      method: 'POST',
      idempotencyKey
    }),

  zefixStatus: () => request<{ eingerichtet: boolean; darfEinrichten: boolean }>('/zefix/status'),

  /*
    Zugang hinterlegen.

    Ein eigener Weg, nicht Teil von /settings – sonst überschriebe jedes
    Speichern der Adresse das Zugangswort mit einem leeren Feld, weil die
    Einstellungsseite es nie gelesen hat und also nichts zurückschicken kann.
  */
  zefixZugangSetzen: (benutzer: string, zugangswort: string, idempotencyKey: string) =>
    request<{ eingerichtet: true }>('/settings/zefix', {
      method: 'PUT',
      body: { benutzer, zugangswort },
      idempotencyKey
    }),

  /*
    Eine Prüfung anfordern.

    Der einzige Weg, den der Betrieb über seinen eigenen Prüfstatus hat. Er
    geht in eine Richtung; entschieden wird beim Betreiber.
  */
  /** Wie viel Platz belegt ist und wie viel das Paket hergibt. */
  speicherstand: () =>
    request<{
      belegtBytes: number
      grenzeBytes: number
      fotosBytes: number
      belegeBytes: number
      logoBytes: number
      paket: string
    }>('/settings/speicher'),

  pruefungAnfordern: (idempotencyKey: string) =>
    request<{ stand: 'pending' }>('/settings/pruefung-anfordern', {
      method: 'POST',
      idempotencyKey
    }),

  zefixZugangEntfernen: (idempotencyKey: string) =>
    request<{ eingerichtet: false }>('/settings/zefix', { method: 'DELETE', idempotencyKey }),

  zefixSuche: (q: string) =>
    request<{ treffer: ZefixTreffer[] }>(`/zefix/suche?q=${encodeURIComponent(q)}`),

  zefixFirma: (uid: string) =>
    request<{ firma: ZefixFirma | null }>(`/zefix/firma/${encodeURIComponent(uid)}`),

  health: () => request<{ status: string; database: string }>('/health')
}

/**
 * Der Datumsbereich einer Belegliste.
 *
 * Eine Achse, nicht drei: `art` sagt, worauf sich `von` und `bis` beziehen.
 * Leere Werte heissen «kein Bereich» und werden nicht gesendet.
 */
export interface Datumsfilter {
  art: string
  von: string
  bis: string
}

export const LEERER_DATUMSFILTER = (art: string): Datumsfilter => ({ art, von: '', bis: '' })

/**
 * Eine Fahrkarte vom Dashboard in eine Liste.
 *
 * Das Dashboard zeigt Zahlen; dahinter liegen Belege. Ein Klick auf «8
 * überfällig» soll nicht den Bereich «Rechnungen» öffnen und dort den
 * Normalfilter zeigen, sondern genau die acht. Also reist mit, was die Liste
 * einstellen muss: welcher Bereich, welcher Filter, welcher Zeitraum.
 *
 * Der Zeitraum ist derselbe `Datumsfilter`, den die Liste ohnehin kennt – die
 * Säulen des Geldstrahls stehen auf Fälligkeitstagen, und genau danach kann
 * die Liste filtern. Kein zweiter Begriff von «Zeitraum».
 */
export interface Sprung {
  bereich: string
  filter?: string
  datum?: Datumsfilter
  /**
   * Woher der Sprung kam.
   *
   * Die Zielliste zeigt den Weg zurück nur, wenn es einen gibt, der stimmt:
   * «← Übersicht» nach einem Klick auf eine Kachel. Wer über den Knopf
   * «Mehrere erfassen» aus den Zahlungen kommt, bekommt ihn nicht – er führte
   * woandershin, als er verspricht.
   */
  herkunft?: string
}

function datumsteile(d?: Datumsfilter): Record<string, string> {
  if (!d || (!d.von && !d.bis)) return {}
  const teile: Record<string, string> = { datumsart: d.art }
  if (d.von) teile.von = d.von
  if (d.bis) teile.bis = d.bis
  return teile
}

/**
 * Die Adresse des Sammel-ZIP.
 *
 * Ein gewöhnlicher Link, damit der Browser den Download selbst erledigt –
 * kein Zwischenspeicher im Speicher der Seite, kein Blob, der bei grossen
 * Archiven den Tab belastet.
 */
export function belegZipWeg(ids: string[]): string {
  return `/documents/pdfs?ids=${ids.join(',')}`
}

/** Die Sammelaktionen, die der Server kennt. Mehr gibt es nicht. */
export type Sammelaktion = 'abschliessen' | 'verwerfen' | 'stornieren'

/**
 * Was eine Sammelaktion zurückbringt.
 *
 * Zwei Listen und keine einzelne Zahl: «vier erledigt, einer abgelehnt» ist
 * der Normalfall, sobald mehrere Belege im Spiel sind, und eine Antwort, die
 * das in einen Erfolg oder einen Fehler presst, verschweigt die Hälfte.
 */
export interface SammelErgebnis {
  aktion: Sammelaktion
  erledigt: Array<{ id: string; nummer: string | null; text: string; pdfFehlt?: boolean }>
  fehler: Array<{ id: string; nummer: string | null; code: string; text: string }>
}

/**
 * Was die Sammelerfassung von Zahlungen zurückbringt.
 *
 * Wieder zwei Listen, aus demselben Grund: Von sieben Rechnungen ist eine
 * schon bezahlt, und das ist keine Fehlermeldung für den ganzen Vorgang.
 */
export interface ZahlungsSammelErgebnis {
  paidAt: string
  erfasst: Array<{
    id: string
    nummer: string | null
    amountRp: number
    status: string
    openRp: number
    text: string
  }>
  fehler: Array<{ id: string; code: string; text: string }>
}

function bueroWeg(
  pfad: string,
  filter: string,
  suche: string,
  extra?: Record<string, string>
): string {
  const p = new URLSearchParams({ filter, ...(extra ?? {}) })
  if (suche.trim()) p.set('suche', suche.trim())
  return `${pfad}?${p.toString()}`
}

/** Die gemeinsame Form aller Büro-Listen. */
export interface BueroListe<Z, F extends string> {
  items: Z[]
  zaehler: Record<F, number>
  summe: Record<string, number>
  /** Wie viele Belege es insgesamt gibt – unabhängig von der Grenze der Abfrage. */
  gesamt?: number
  /** Wie viele davon gelesen wurden. */
  gelesen?: number
  /** Die Abfrage hat ihre Grenze erreicht: Liste UND Zähler sind eine Teilmenge. */
  gekuerzt?: boolean
}

export type RechnungsFilter =
  'alle' | 'entwurf' | 'offen' | 'ueberfaellig' | 'bezahlt' | 'gesendet' | 'storniert'

export interface BueroRechnung {
  id: string
  number: string | null
  status: string
  issueDate: string | null
  dueDate: string | null
  customerId: string
  customerName: string
  projectName: string | null
  totalRp: number
  paidRp: number
  offenRp: number
  zahlstatus: 'offen' | 'teilbezahlt' | 'bezahlt' | 'ueberbezahlt' | 'storniert'
  ueberfaellig: boolean
  hasPdf: boolean
  lineCount: number
  nextActionLabel: string | null
  nextActionDate: string | null
  nextActionOffen: boolean
}

export type KundenFilter = 'aktiv' | 'offen' | 'archiviert' | 'alle'

export interface BueroKunde {
  id: string
  name: string
  contact: string | null
  street: string | null
  zip: string | null
  city: string | null
  email: string | null
  phone: string | null
  oneOff: boolean
  active: boolean
  projekte: number
  rechnungen: number
  offerten: number
  umsatzRp: number
  offenRp: number
  letztesDatum: string | null
  letzteNummer: string | null
}

export interface BueroBeleg {
  id: string
  type: 'rechnung' | 'offerte'
  number: string | null
  status: string
  issueDate: string | null
  dueDate?: string | null
  validUntil?: string | null
  totalRp: number
  offenRp: number
  zahlstatus?: string
  ueberfaellig?: boolean
  zustand?: string | null
  projectName?: string | null
  hasPdf: boolean
}

export interface KundenBlatt {
  kunde: {
    id: string
    name: string
    contact: string | null
    street: string | null
    zip: string | null
    city: string | null
    email: string | null
    phone: string | null
    notes: string | null
    oneOff: boolean
    active: boolean
    version: number
  }
  projekte: Array<{
    id: string
    name: string
    street: string | null
    zip: string | null
    city: string | null
    status: string
    rapporte: number
  }>
  belege: BueroBeleg[]
  summe: { offenRp: number; umsatzRp: number; rechnungen: number; offerten: number }
}

export type ProjektFilter = 'offen' | 'unverrechnet' | 'abgeschlossen' | 'archiviert' | 'alle'

export interface BueroProjekt {
  id: string
  name: string
  street: string | null
  zip: string | null
  city: string | null
  status: string
  customerId: string
  customerName: string
  rapporte: number
  unverrechnet: number
  letzteArbeit: string | null
  belege: number
  verrechnetRp: number
}

export interface ProjektBlatt {
  projekt: {
    id: string
    name: string
    street: string | null
    zip: string | null
    city: string | null
    status: string
    notes: string | null
    customerId: string
    customerName: string
    version: number
  }
  rapporte: Array<{
    id: string
    workDate: string
    isRegie: boolean
    signiert: boolean
    userName: string
    zeilen: number
    unverrechnet: number
  }>
  belege: BueroBeleg[]
}

export type KatalogFilter = 'alle' | 'arbeit' | 'material' | 'unbenutzt' | 'inaktiv'

export interface BueroLeistung {
  id: string
  code: string | null
  name: string
  unit: string
  unitPriceRp: number
  vatRate: number
  kind: 'arbeit' | 'material'
  active: boolean
  version: number
  verwendungen: number
  letzteVerwendung: string | null
  umsatzRp: number
}

export interface Payment {
  id: string
  amountRp: number
  paidAt: string
  source: 'manuell' | 'camt'
  note: string | null
  createdAt: string
  voidedAt: string | null
  voidReason: string | null
  byName: string | null
  voidedByName: string | null
}

export interface Zahlungsstand {
  totalRp: number
  paidRp: number
  openRp: number
  overpaidRp: number
  status: 'offen' | 'teilbezahlt' | 'bezahlt' | 'ueberbezahlt' | 'storniert'
}

export interface OffenerPunkt {
  art: string
  dringlichkeit: 1 | 2 | 3
  id: string
  titel: string
  text: string
  aktion: string
  ziel: { typ: 'beleg' | 'rapporte'; id: string }
  totalRp?: number
  offenRp?: number
  dueDate?: string | null
}

/**
 * Was auf dem Dashboard steht.
 *
 * Zwei Dinge stehen dort nebeneinander und dürfen nie verwechselt werden:
 * `aufgaben` hat ein Mensch geschrieben und wird abgehakt, `hinweise` rechnet
 * der Server bei jedem Aufruf neu und verschwindet erst, wenn die Ursache weg
 * ist. Deshalb zwei Felder und nicht eine gemischte Liste.
 */
export interface DashboardAufgabe {
  id: string
  label: string | null
  date: string | null
  note: string | null
  customerName: string
  projectName: string | null
  number: string | null
  status: string
  type: string
  totalRp: number
  offenRp: number | null
}

export interface DashboardRechnung {
  id: string
  number: string
  customerName: string
  issueDate: string
  dueDate: string | null
  totalRp: number
  paidRp: number
  offenRp: number
  /** Abgeleitet aus den gültigen Zahlungen – nirgends gespeichert. */
  zahlstatus: 'offen' | 'teilbezahlt' | 'bezahlt' | 'ueberbezahlt' | 'storniert'
  belegstatus: string
  ueberfaellig: boolean
  /**
   * Hat der Beleg den Betrieb verlassen?
   *
   * Nicht dasselbe wie `belegstatus === 'gesendet'`: der Versandweg über
   * `/documents/:id/versand` setzt nur einen Zeitstempel. Eine Rechnung, die
   * nicht draussen ist, wird nie überfällig – der Kunde weiss nichts von ihr.
   */
  versendet: boolean
}

export interface DashboardRapportProjekt {
  projectId: string
  projectName: string
  customerName: string
  anzahl: number
  aeltester: string
  preisOffen: number
  ohneFoto: number
}

export interface DashboardDaten {
  heute: string
  aufgaben: {
    ueberfaellig: DashboardAufgabe[]
    heute: DashboardAufgabe[]
    naechste7: DashboardAufgabe[]
    ohneDatum: DashboardAufgabe[]
  }
  /** Die Aufgabenabfrage hat ihre Grenze erreicht – es gibt mehr als gezeigt. */
  aufgabenGekuerzt?: boolean
  hinweise: OffenerPunkt[]
  hinweiseSumme: { gesamt: number; probleme: number; ueberfaellig: number; offenRp: number }
  /*
    Zu jeder gekürzten Liste gehören zwei Angaben: wie viele es insgesamt sind
    und ob gekürzt wurde. Die SUMMEN daneben sind nie gekürzt – sie kommen aus
    eigenen Abfragen ohne Grenze.
  */
  rechnungen: {
    items: DashboardRechnung[]
    summe: {
      anzahl: number
      offenRp: number
      ueberfaelligAnzahl: number
      ueberfaelligRp: number
    }
    gesamt?: number
    gekuerzt?: boolean
  }
  rapporte: {
    projekte: DashboardRapportProjekt[]
    summe: { projekte: number; rapporte: number; preisOffen: number }
    gesamt?: number
    gekuerzt?: boolean
  }
  offerten: {
    gebaut: boolean
    hinweis?: string
    offen?: OffertZeile[]
    laufenAb?: OffertZeile[]
    abgelaufen?: OffertZeile[]
    angenommenOhneRechnung?: OffertZeile[]
    summe?: { offenRp: number; angenommenRp: number }
    gesamt?: {
      offen: number
      laufenAb: number
      abgelaufen: number
      angenommenOhneRechnung: number
    }
    gekuerzt?: boolean
  }
}

export type OffertFilter =
  'entwurf' | 'offen' | 'angenommen' | 'abgelehnt' | 'abgelaufen' | 'storniert' | 'alle'

/**
 * Eine Offerte in der Liste.
 *
 * `zustand` rechnet der Server aus Belegzustand, Antwort des Kunden und Datum.
 * "Abgelaufen" steht nirgends in der Datenbank – es ergibt sich, und deshalb
 * stimmt es auch am Tag nach dem Ablauf ohne einen nächtlichen Lauf.
 */
export interface OffertZeile {
  id: string
  number: string | null
  status: string
  customerId: string
  customerName: string
  projectName: string | null
  issueDate: string | null
  validUntil: string | null
  totalRp: number
  discountPercent: string | number
  outcome: 'angenommen' | 'abgelehnt' | null
  outcomeNote: string | null
  nextActionLabel: string | null
  nextActionDate: string | null
  hasPdf: boolean
  rechnungen: number
  zustand: 'entwurf' | 'offen' | 'angenommen' | 'abgelehnt' | 'abgelaufen' | 'storniert'
}

export interface OffertenListe {
  items: OffertZeile[]
  zaehler: Record<OffertFilter, number>
  summe: { anzahl: number; totalRp: number; offenRp: number }
}

export type ZahlungsFilter =
  'offen' | 'ueberfaellig' | 'teilbezahlt' | 'bezahlt' | 'ueberbezahlt' | 'storniert' | 'alle'

export interface ZahlungsZeile {
  id: string
  number: string
  customerName: string
  projectName: string | null
  issueDate: string
  dueDate: string | null
  totalRp: number
  paidRp: number
  offenRp: number
  ueberbezahltRp: number
  /** Abgeleitet aus den gültigen Zahlungen – nirgends gespeichert. */
  zahlstatus: 'offen' | 'teilbezahlt' | 'bezahlt' | 'ueberbezahlt' | 'storniert'
  /** Der Belegzustand steht daneben, nie darin: gesendet ist nicht bezahlt. */
  belegstatus: string
  ueberfaellig: boolean
  zahlungen: number
}

/**
 * Der Zeitraum des Zahlungseingangs.
 *
 * Er gilt NUR für eingegangenes Geld. Forderungen haben keinen Zeitraum: eine
 * Rechnung, die seit vier Monaten offen ist, gehört auf jeden Blick.
 */
export type Zeitraum = 'monat' | 'jahr' | 'alles'

/**
 * Die Finanzlage des Betriebs – über ALLE Rechnungen, nicht über die Auswahl.
 *
 * Gerechnet in SQL (`services/belegsql.ts`), damit die Zahl nicht an der
 * Listengrenze hängt. Jede dieser Zahlen hat genau einen Filter, der sie
 * ergibt – das ist der Grund, weshalb die Kacheln anklickbar sind.
 */
export interface Finanzlage {
  offenRp: number
  ueberfaelligAnzahl: number
  ueberfaelligRp: number
  teilbezahltAnzahl: number
  teilbezahltRp: number
  ueberbezahltAnzahl: number
  /** Was zu viel eingegangen ist. Wird gezeigt und nie automatisch verrechnet. */
  ueberbezahltRp: number
}

/** Geld, das tatsächlich hereingekommen ist – nicht Rechnungsvolumen. */
export interface Zahlungseingang {
  zeitraum: Zeitraum
  vonTag: string | null
  bisTag: string | null
  anzahl: number
  betragRp: number
}

export interface ZahlungsListe {
  items: ZahlungsZeile[]
  zaehler: Record<ZahlungsFilter, number>
  /** Was die gezeigte Auswahl ergibt. Für die Lage des Betriebs steht `lage`. */
  summe: { offenRp: number; totalRp: number }
  lage?: Finanzlage
  eingang?: Zahlungseingang
  gesamt?: number
  gelesen?: number
  gekuerzt?: boolean
}

export interface HistorieZeile {
  id: string
  documentId: string
  number: string
  customerName: string
  amountRp: number
  paidAt: string
  source: string
  note: string | null
  createdAt: string
  voidedAt: string | null
  voidReason: string | null
  byName: string | null
  voidedByName: string | null
}

export interface ZahlungsHistorie {
  payments: HistorieZeile[]
  /** Welcher Zeitraum gezeigt wird – damit der Bildschirm ihn benennen kann. */
  zeitraum?: Zeitraum
  vonTag?: string | null
  bisTag?: string | null
  summe: { anzahl: number; gueltig: number; storniert: number; betragRp: number }
}

export interface FinalizeAntwort {
  documentId?: string
  id?: string
  number: string
  reference?: string
  referenceType?: 'QRR' | 'SCOR'
  totalRp?: number
  status: string
  pdfReady: boolean
  pdfSha256?: string
  /** Gesetzt, wenn die Nummer vergeben ist, das PDF aber scheiterte. */
  error?: string
}

export interface SendAttempt {
  id: string
  toEmail: string
  subject: string
  bodyText?: string
  status: 'laeuft' | 'gesendet' | 'zugestellt' | 'fehlgeschlagen' | 'unklar'
  provider: string
  providerMessageId: string | null
  errorText: string | null
  pdfSha256: string | null
  createdAt: string
  deliveredAt?: string | null
  byName?: string | null
  documentStatus?: string
}

// ---------------------------------------------------------------------------

export type DocumentStatus =
  | 'entwurf'
  | 'wird_abgeschlossen'
  | 'nummer_ohne_pdf'
  | 'bereit'
  | 'wird_gesendet'
  | 'gesendet'
  | 'versand_fehlgeschlagen'
  | 'versand_unklar'
  | 'storniert'

export interface RapportFilter {
  status?: 'alle' | 'offen' | 'verrechnet'
  projectId?: string
  userId?: string
  signed?: 'ja' | 'nein'
  priceOpen?: boolean
  withPhotos?: boolean
}

export interface OfficeRapport {
  id: string
  projectId: string
  projectName: string
  customerName: string
  userId: string
  userName: string
  workDate: string
  description: string
  isRegie: boolean
  signed: boolean
  signedByName: string | null
  hours: string
  lineCount: string
  photoCount: string
  openPriceCount: string
  /** Zeilen, die noch in keinem Beleg liegen. 0 heisst: vollständig verrechnet. */
  openLineCount: string
  billed: boolean
  documentId: string | null
}

export interface OpenLine {
  id: string
  rapportId: string
  kind: 'stunden' | 'material'
  description: string
  quantity: string
  unit: string
  unitPriceRp: number | null
  isExtra: boolean
  isFreetext: boolean
  workDate: string
  isRegie: boolean
  signed: boolean
  projectId: string
  projectName: string
  customerId: string
  customerName: string
  userName: string
}

export interface DocumentSummary {
  id: string
  /** Rechnung oder Offerte – beide liegen in derselben Tabelle. */
  type: 'rechnung' | 'offerte'
  number: string | null
  status: DocumentStatus
  customerId: string
  customerName: string
  projectName: string | null
  issueDate: string
  totalRp: number
  lineCount: string
}

/**
 * Kopfdaten eines direkt geschriebenen Belegs.
 *
 * Die Objektadresse steht neben der Kundenadresse, nicht anstelle: die
 * Rechnung geht an die Verwaltung, gearbeitet wurde in der Wohnung.
 */
export interface ExpressKopf {
  id?: string
  type?: 'rechnung' | 'offerte'
  /** Belegdatum. Leer heisst: heute. */
  issueDate?: string | null
  /** Nur bei der Offerte. Leer heisst: Standardgültigkeit des Betriebs. */
  validUntil?: string | null
  customerId: string
  projectId?: string | null
  projectName?: string | null
  objectStreet?: string | null
  objectZip?: string | null
  objectCity?: string | null
  serviceFrom?: string | null
  serviceTo?: string | null
  paymentTermDays?: number | null
  notes?: string | null
  conditionsSnapshot?: string | null
}

/**
 * Vorlage – der Vorschlag, nicht der Beleg.
 *
 * Alles hier ist ein Startwert für ein Formular. Was der Mensch danach ändert,
 * ändert die Vorlage nicht; was die Vorlage später ändert, ändert eine längst
 * geschriebene Rechnung nicht.
 */
export interface TemplateSummary {
  id: string
  name: string
  description: string
  type: 'rechnung' | 'offerte'
  active: boolean
  lineCount: string
  netRp: number
}

export interface TemplateDetail extends TemplateSummary {
  defaultProjectName: string | null
  defaultObjectStreet: string | null
  defaultObjectZip: string | null
  defaultObjectCity: string | null
  defaultPaymentTermDays: number | null
  defaultNotes: string
  defaultDiscountPercent: string | number
  conditionsText: string | null
  version: number
}

export interface TemplateLine {
  id: string
  pos: number
  description: string
  quantity: string
  unit: string
  unitPriceRp: number
  vatRate: string
  discountPercent: string
  kind: 'arbeit' | 'material'
  sourceCatalogItemId: string | null
}

export interface NewTemplate {
  name: string
  description?: string
  type?: 'rechnung' | 'offerte'
  defaultProjectName?: string | null
  defaultObjectStreet?: string | null
  defaultObjectZip?: string | null
  defaultObjectCity?: string | null
  defaultPaymentTermDays?: number | null
  defaultNotes?: string
  defaultDiscountPercent?: number
  conditionsText?: string | null
  lines: Array<{
    description: string
    quantity: number
    unit: string
    unitPriceRp: number
    vatRate: number
    discountPercent: number
    kind: 'arbeit' | 'material'
  }>
}

/**
 * Antwort auf „Aus letzter Rechnung".
 *
 * Es fehlen absichtlich Nummer, PDF, Zahlungen, Versandstatus und der nächste
 * Schritt – nicht als leere Felder, sondern gar nicht. Was die Antwort nicht
 * enthält, kann die Oberfläche auch nicht versehentlich übernehmen.
 */
export interface LetzteRechnung {
  found: boolean
  message?: string
  source?: { id: string; number: string | null; issueDate: string }
  head?: {
    customerId: string
    customerName: string
    projectName: string | null
    objectStreet: string | null
    objectZip: string | null
    objectCity: string | null
    paymentTermDays: number | null
    discountPercent: number
    notes: string
  }
  lines?: Array<{
    description: string
    quantity: string
    unit: string
    unitPriceRp: number
    vatRate: string
    discountPercent: string
    kind: 'arbeit' | 'material'
  }>
}

/** Die Antwort von GET /documents/:id/versandwege. */
export interface Versandwege {
  pdfDa: boolean
  storniert: boolean
  benutzerMail: string
  firmaMail: string
  /** Sind persönliche und Firmenadresse dieselbe? Dann braucht es keine Auswahl. */
  adressenGleich: boolean
  kundenMail: string
  kundenversand: { moeglich: boolean; grund: string | null }
  gesendet: {
    anBenutzer: string | null
    anFirma: string | null
    anKunde: string | null
  }
  /**
   * Der Vermerk «von Hand versendet» – gedruckt und per Post, übergeben.
   *
   * Steht neben `gesendet` und nicht darin: dort steht, was Raporto
   * nachweislich verschickt hat, hier, was ein Mensch bestätigt. Die
   * Oberfläche nennt es deshalb «vermerkt» und nie «gesendet».
   */
  vermerk?: {
    moeglich: boolean
    grund: string
    am: string | null
    von: string | null
  }
}

export interface SmtpEingabe {
  aktiv: boolean
  host: string
  port: number
  benutzer: string
  /** Leer heisst «unverändert» – das Passwort kommt nie zurück. */
  passwort?: string
  absender: string
  antwortAn: string
}

export interface DocumentDetail extends DocumentSummary {
  projectName: string | null
  /** Gesetzt heisst: der Beleg hängt an einer Baustelle, nicht nur an Text. */
  projectId: string | null
  objectStreet: string | null
  objectZip: string | null
  objectCity: string | null
  serviceFrom: string | null
  serviceTo: string | null
  discountPercent: string | number
  discountAmountRp: number
  paymentTermDays: number | null
  nextActionLabel: string | null
  nextActionDate: string | null
  nextActionNote: string | null
  nextActionDoneAt: string | null
  street: string | null
  zip: string | null
  city: string | null
  customerEmail: string | null
  notes: string
  conditionsSnapshot: string
  subtotalRp: number
  vatTotalRp: number
  version: number
  dueDate: string | null
  structuredReference: string | null
  referenceType: 'QRR' | 'SCOR' | null
  pdfSha256: string | null
  hasPdf: boolean
  lastError: string | null
  /** Nur bei einer Rechnung, die aus einer Offerte entstand. */
  sourceQuoteNumber?: string | null
  finalizedAt: string | null
  /** Nur bei der Offerte gefüllt. */
  validUntil: string | null
  quoteOutcome: 'angenommen' | 'abgelehnt' | null
  quoteOutcomeNote: string | null
  sourceQuoteId: string | null
  /*
    Der Versandstand – die Grundlage dafür, ob die Maske «Bearbeiten» zeigt.

    Ohne diese vier Felder kann die Oberfläche «abgeschlossen» nicht von
    «versendet» unterscheiden, und genau daran scheiterte sie nach PR #15:
    Sie fragte ersatzweise nach `status === 'entwurf'` und sperrte damit jede
    nummerierte Rechnung, auch die, die nie jemand bekommen hat.

    Ausgewertet wird nicht hier, sondern mit `bearbeitungssperre` aus
    `@raporto/shared` – derselben Funktion, die der Server benutzt. Zwei
    Umsetzungen derselben Regel laufen irgendwann auseinander; eine kann das
    nicht.
  */
  gesendetAnKundeAm: string | null
  gesendetAnFirmaAm: string | null
  gesendetAnBenutzerAm: string | null
  versendetVermerktAm: string | null
}

export interface DocumentLine {
  id: string
  pos: number
  description: string
  quantity: string
  unit: string
  unitPriceRp: number
  vatRate: string
  discountPercent: string
  lineTotalRp: number
  kind: 'arbeit' | 'material'
  isExtra: boolean
  fromFreetext: boolean
  sourceRapportLineId: string | null
  sourceCatalogItemId: string | null
}

export interface DocumentTotals {
  /**
   * Netto der Positionen VOR dem Gesamtrabatt, dazu der Abzug.
   *
   * Ohne diese beiden Zahlen stand in der Summenbox ein Netto, das kleiner
   * war als die Summe der Zeilen darüber, und nichts erklärte den
   * Unterschied. Fehlen sie (ältere Antwort), zeigt die Oberfläche die
   * Herleitung einfach nicht.
   */
  zwischentotalRp?: number
  rabattProzent?: number
  rabattRp?: number
  subtotalRp: number
  vatTotalRp: number
  /** Differenz der Fünf-Rappen-Rundung. 0 heisst: nicht gerundet. */
  rundungRp?: number
  /** Das Endtotal vor der Rundung. */
  ungerundetRp?: number
  totalRp: number
  vatBySatz?: Array<{ rate: number; baseRp: number; vatRp: number }>
}

export interface NewDocumentLine {
  description: string
  quantity: number
  unit: string
  unitPriceRp: number
  vatRate: number
  discountPercent?: number
  kind?: 'arbeit' | 'material'
  isExtra?: boolean
}

export interface ToCatalogAnswer {
  created: boolean
  similar: Array<{ id: string; code: string; name: string; unitPriceRp: number }>
  vorschlag?: { code: string; name: string; unit: string; unitPriceRp: number }
  catalogItem?: { id: string; code: string; name: string; unitPriceRp: number }
  message: string
}

/** Ein Treffer aus dem Handelsregister – Name, UID, Rechtsform, Sitz. */
export interface ZefixTreffer {
  name: string
  uid: string
  rechtsform: string
  sitz: string
  aktiv: boolean
}

/** Dieselbe Firma mit Adresse. Sie kommt erst auf Nachfrage zu einer UID. */
export interface ZefixFirma extends ZefixTreffer {
  street: string
  zip: string
  city: string
}

/* ---------------------------------------------------------------------------
   Betreibercockpit

   Die Sicht von Raporto auf seine Kunden. Keine Geschäftsdaten der Betriebe –
   nur Stammdaten, Nutzungszahlen und der Stand der Testphase.
   --------------------------------------------------------------------------- */

export interface BetreiberPaket {
  key: string | null
  name: string | null
  /** Der Listenpreis des gewählten Pakets. KEIN Nachweis, dass bezahlt wurde. */
  listenpreisRp: number | null
}

export interface BetreiberMandantZeile {
  id: string
  name: string
  kuerzel: string
  nummer: number
  erstelltAm: string
  /**
   * Die alte Spalte. Historisch und bei jedem Betrieb 'test' – sie wird nicht
   * umgedeutet und steht nur noch neben dem Lifecycle, bis sie verschwindet.
   */
  status: string
  /** Paket 7 · Der Vertragszustand. Geschrieben wird er nur im Server. */
  lifecycle: string
  istTestTenant: boolean
  adminEmail: string | null
  adminName: string | null
  benutzer: number
  benutzerAktiv: number
  letzteAnmeldung: string | null
  paket: BetreiberPaket
  testphase: { bis: string | null; abgelaufen: boolean }
  kunden: number
  projekte: number
  rechnungen: number
  offerten: number
  rapporte: number
  rechnungenMonat: number
  offertenMonat: number
  rapporteMonat: number
  letzteAktivitaet: string | null
  /** Gesetzt heisst: dieser Betrieb kommt an keiner Route mehr vorbei. */
  gesperrt: { seit: string; grund: string } | null
  /** Nur eine Vormerkung – es ist nichts gelöscht. */
  loeschung: { seit: string; grund: string } | null
  /**
   * Was der Betrieb WIRKLICH bezahlt – aus `tenant_abrechnung`.
   *
   * `paket` daneben ist die Berechtigung, dies hier der Beleg dafür. Stripe-
   * Kennungen stehen bewusst nicht drin; sie gehören ins Detail.
   */
  abo: {
    status: string | null
    bezahltesPaket: string | null
    intervall: string | null
    periodeBis: string | null
    kuendigungZumEnde: boolean
  }
}

export interface BetreiberListe {
  heute: string
  monatAb: string
  mandanten: BetreiberMandantZeile[]
  /** Wie viele Betriebe es insgesamt gibt – `mandanten` kann gekürzt sein. */
  gesamt: number
  /** Steht `zeigt x von y` in der Kopfzeile der Liste? */
  gekuerzt: boolean
  mitZahlen: boolean
  summe: {
    betriebe: number
    betriebeMitAktivitaet: number
    benutzer: number
    rechnungenMonat: number
    offertenMonat: number
    rapporteMonat: number
    testphaseAbgelaufen: number
    gesperrt: number
    loeschungVorgemerkt: number
    neuLetzte30Tage: number
  }
  abrechnung: {
    verbunden: boolean
    hinweis: string
    testphaseWirktNicht: boolean
    statusWirdNieGeaendert: boolean
  }
}

export interface BetreiberStammdaten {
  firma: string | null
  firmaRechtlich: string | null
  strasse: string | null
  plz: string | null
  ort: string | null
  uid: string | null
  branche: string | null
  mail: string | null
  telefon: string | null
  ansprechpartnerMail: string | null
  ansprechpartnerTelefon: string | null
  verantwortlich: string | null
  verantwortlichMail: string | null
  pruefstand: string | null
  /** Es gibt keine Spalte dafür. Die Oberfläche zeigt «nicht erfasst». */
  sprache: null
  /** Dasselbe – geraten wird nichts. */
  land: null
}

/**
 * Was der Betreiber über die Endkunden eines Betriebs erfährt: wie viele es
 * sind, und dass er sie nicht sieht.
 *
 * Hier stand vorher `BetreiberKundenkartei` mit Name, Ort, Mail, Telefon,
 * Belegzahl und Erfassungsdatum je Kunde. Die Begründung für den Wegfall steht
 * in `routes/betreiber.ts`.
 *
 * `einsehbar` ist heute immer false. Das Feld ist trotzdem da, weil der
 * befristete Supportzugriff es später auf true setzt – dann ändert sich die
 * Oberfläche und nicht der Vertrag zwischen Server und Browser.
 */
export interface BetreiberEndkunden {
  anzahl: number
  einsehbar: boolean
  hinweis: string
}

/**
 * Paket 10 · Das Paket im Detail – mit Plätzen und Verlauf.
 *
 * Die Liste bekommt weiter nur `BetreiberPaket`: Für Plätze und Verlauf wäre
 * je Betrieb eine eigene Abfrage nötig, und die Liste überfliegt man.
 */
export interface BetreiberPaketDetail extends BetreiberPaket {
  /** Aktive Zugänge. Deaktivierte zählen nicht mit. */
  benutzer: number
  /** Was das Paket erlaubt – oder null, wenn kein Paket feststeht. */
  benutzerGrenze: number | null
  /**
   * Ob die Durchsetzung heute schon eingeschaltet ist.
   *
   * **Nicht für die Anzeige.** Das ist der Stand eines internen
   * Rolloutschalters, und der ist keine Produktaussage: Das Paketlimit gilt,
   * ob wir es heute schon technisch erzwingen oder nicht. Das Feld bleibt in
   * der Antwort, weil es für die Abnahme des Rollouts gebraucht wird – die
   * Oberfläche zeigt es nicht.
   */
  grenzeAktiv: boolean
  ueberGrenze: boolean
  verlauf: Array<{
    von: string | null
    nach: string | null
    grund: string
    actor: string | null
    wann: string
  }>
}

/** Paket 10 · Was ein Paketwechsel bedeuten würde. Reine Auskunft. */
export interface BetreiberPaketVorschau {
  vonPlanKey: string | null
  vonName: string | null
  nachPlanKey: string | null
  nachName: string | null
  richtung: 'hoch' | 'runter' | 'seitlich' | 'gleich'
  benutzer: number
  benutzerGrenzeNeu: number | null
  belegtBytes: number
  grenzeBytesNeu: number
  hindernisse: Array<{ art: 'benutzer' | 'speicher'; text: string }>
  moeglich: boolean
}

export interface BetreiberPaketErgebnis {
  ok: boolean
  geaendert: boolean
  von: string | null
  nach: string | null
  hinweis: string
}

export interface BetreiberMandant extends Omit<BetreiberMandantZeile, 'letzteAnmeldung' | 'paket'> {
  heute: string
  /**
   * Was auf diesem Stand möglich ist – heute genau eine Frage.
   *
   * Die Sofortlöschung hängt an einem Schalter der Umgebung. Die Oberfläche
   * fragt ihn hier ab, statt die Handlung anzubieten und den Betreiber in
   * eine Fehlermeldung laufen zu lassen, nachdem er alles ausgefüllt hat.
   */
  moeglich: { sofortloeschung: boolean }
  paket: BetreiberPaketDetail
  admin: {
    name: string | null
    mail: string | null
    telefon: string | null
    rolle: string
    zweiterFaktor: boolean
    letzteAnmeldung: string | null
  } | null
  stammdaten: BetreiberStammdaten
  /** Paket 9 · Der gemessene Speicherstand. Echte Werte, keine Schätzung. */
  speicher: {
    belegtBytes: number
    grenzeBytes: number
    gbBelegt: number
    gbGrenze: number
    prozent: number
    restBytes: number
    fotosBytes: number
    belegeBytes: number
    logoBytes: number
    paket: string
    planKey: string | null
    marken: Array<{
      schwelle: number
      erreichtAm: string | null
      prozent: number
      /** false heisst: Schwelle erreicht, aber keine eigene Mail – siehe Paket 9. */
      benachrichtigt: boolean
    }>
    zuletztGewarnt: string | null
  }
  /** Paket 7 · Vertragszustand und Verlauf – ausschliesslich lesend. */
  vertrag: {
    lifecycle: string
    istTestTenant: boolean
    beginn: string | null
    gekuendigtAm: string | null
    wirksamPer: string | null
    graceBis: string | null
    reaktiviertAm: string | null
    /** Gesetzt, solange der Betrieb nicht klassifiziert ist. */
    hinweis: string | null
    /** Wohin dieser Betrieb von hier aus wechseln darf – aus der Übergangstabelle. */
    moeglicheZiele: string[]
    verlauf: Array<{
      von: string | null
      nach: string
      grund: string
      actor: string | null
      wann: string
    }>
  }
  endkunden: BetreiberEndkunden
  team: Array<{
    name: string
    email: string
    rolle: string
    seit: string
    letzteAnmeldung: string | null
    aktiv: boolean
    zweiterFaktor: boolean
  }>
  abrechnung: { verbunden: boolean; hinweis: string }
}

/**
 * Paket 12 · Was die Control Plane beim Öffnen zeigt.
 *
 * Kein Umsatz, kein MRR: `abrechnung.verbunden` ist false, und die Oberfläche
 * zeigt dort «—». Eine Zahl wäre erfunden.
 */
export interface BetreiberUebersicht {
  heute: string
  betriebe: {
    /** Nur echte Betriebe – Testbetriebe zählen hier nicht mit. */
    gesamt: number
    /** Alles zusammen, für die Liste. */
    gesamtMitTest: number
    testbetriebe: number
    gesperrt: number
    vorgemerkt: number
    trialAbgelaufen: number
    nachLifecycle: Record<string, number>
    nachPaket: Record<string, number>
  }
  benutzer: { gesamt: number }
  speicher: {
    belegtBytes: number
    grenzeBytes: number
    prozent: number
    voll: number
    ueberSchwelle: number
    /** Über wie viele Betriebe gerechnet wurde. */
    gemessen: number
    vollstaendig: boolean
    /** Die Kennungen der Betriebe über 80 % – damit der Klick sie findet. */
    kritisch: string[]
  }
  automatik: {
    zeitplanAktiv: boolean
    versandAktiv: boolean
    speicherlimitAktiv: boolean
    benutzergrenzeAktiv: boolean
    aboSelbstbedienung: boolean
    /** Die Jobs, die der Prozess kennt – unabhängig davon, ob schon einer lief. */
    angemeldet: string[]
    jobs: Array<{
      job: string
      aktiv: boolean
      laeuft: boolean
      letztesEnde: string | null
      letztesErgebnis: string | null
      letzterFehler: string | null
    }>
  }
  nachrichten: Record<string, number>
  aufmerksamkeit: Array<{
    art: string
    text: string
    anzahl: number
    schwere: string
    /** Wohin der Klick führt – oder null, wenn dieser Punkt keine Liste hat. */
    filter: string | null
  }>
  letzteAktionen: Array<{ betrieb: string; betreiber: string; aktion: string; wann: string | null }>
  /** Die Abo-Bestände. Beträge stehen in `BetreiberWirtschaft`, nicht hier. */
  abo: Abolage
}

/** Bestände aus `tenant_abrechnung` – Zahlen ohne Franken. */
export interface Abolage {
  zahlendAktiv: number
  testphaseLaufend: number
  testphaseAbgelaufen: number
  zahlungsproblem: number
  gekuendigt: number
  ohneAbo: number
}

/**
 * Die wirtschaftliche Lage von Raporto selbst.
 *
 * Zwei Begriffe, die nie vermischt werden: `mrr` ist, was die laufenden Abos
 * pro Monat wert sind; `geldeingang` ist, was im Zeitraum wirklich bezahlt
 * wurde. Der eine ist netto nach Listenpreis, der andere brutto nach
 * Kontoauszug – deshalb sind sie nicht ineinander umrechenbar.
 */
export interface BetreiberWirtschaft {
  heute: string
  lage: Abolage
  stripeVerbunden: boolean
  mrr: {
    betragRp: number
    waehrung: string | null
    posten: Array<{ paket: string; intervall: string; anzahl: number; proMonatRp: number }>
    ohnePreis: Array<{ paket: string | null; intervall: string | null; anzahl: number }>
    waehrungenGemischt: string[]
    preislisteGelesen: boolean
    hinweis: string | null
  }
  geldeingang: {
    zeitraum: BetreiberZeitraum
    vonTag: string | null
    bisTag: string | null
    betragRp: number
    anzahl: number
    waehrung: string | null
    waehrungenGemischt: string[]
    verlauf: Array<{ monat: string; betragRp: number; anzahl: number }>
    gelesen: boolean
    hinweis: string | null
  }
}

export type BetreiberZeitraum = 'monat' | 'jahr' | 'alles'

/** Paket 13 · Was ein Purge anfassen würde. Reine Auskunft. */
export interface BetreiberPurgeUmfang {
  name: string
  istTestTenant: boolean
  lifecycle: string
  umfang: {
    benutzer: number
    nurHierBenutzer: number
    kunden: number
    projekte: number
    belege: number
    rapporte: number
    fotos: number
    sitzungen: number
    geraete: number
  }
}

export interface BetreiberPurgeErgebnis {
  ok: boolean
  geloescht: string
  benutzerGeloescht: number
  benutzerBehalten: number
  dateienEntfernt: number
  /** Leer ist der Normalfall. Nicht leer heisst: Dateien blieben im Speicher. */
  dateiFehler: string[]
  hinweis: string
}

/**
 * Paket D8 · Das Ergebnis einer Sofortloeschung.
 *
 * `zustand` ist das Feld, auf das es ankommt. `erledigt` heisst gelöscht;
 * alles andere heisst NICHT gelöscht – der Vorgang steht dann sichtbar und
 * wird wieder aufgenommen. Die Oberflaeche darf daraus keinen Erfolg machen.
 */
export interface BetreiberSofortloeschung {
  ok: boolean
  opId: string
  zustand: string
  name: string
  schritte: string[]
  hinweise: string[]
  stripeBeendet: boolean
  zahlungsmittelEntfernt: number
  customerEntfernt: boolean
  dateienEntfernt: number
  benutzerGeloescht: number
  benutzerBehalten: number
  hinweis: string
}

export interface BetreiberProtokoll {
  eintraege: Array<{
    betriebId: string | null
    betrieb: string
    betreiber: string
    aktion: string
    angaben: Record<string, unknown>
    wann: string
  }>
}

/* ═══════════════════════════════════════════════════════════════════════════
   Kita · Betreuung
   ═══════════════════════════════════════════════════════════════════════════ */

export type Betreuungsart = 'ganztag' | 'halbtag_ohne' | 'halbtag_mit'

export interface Betreuungsposten {
  art: Betreuungsart
  /** Betreuungstage pro Woche. Dezimal: 0.25, 0.5, 0.75, 1.25, 2.5 … */
  tageProWoche: number
  /** Vereinbarter Preis je Tag/Woche in Rappen. `null` = Katalogpreis. */
  einzelpreisRp?: number | null
  bezeichnung?: string | null
}

export interface Betreuungseingabe {
  kindName: string
  geburtsdatum: string
  customerId: string
  beginn: string
  ende?: string | null
  geschwisterrabatt?: boolean
  fixbetragRp?: number | null
  fixbetragText?: string | null
  hinweis?: string | null
  /**
   * Eingewöhnung vereinbart – das Kästchen, wie beim Geschwisterrabatt.
   *
   * Der Zeitraum steht in den beiden Daten darunter; der erste reguläre Tag
   * folgt daraus und rechnet `eingewoehnungszeitraum()` aus `@raporto/shared`
   * – dieselbe Funktion, mit der der Server abrechnet.
   */
  eingewoehnung?: boolean
  /** Erster Tag der Eingewöhnung, frei gewählt – `YYYY-MM-DD`. */
  eingewoehnungVon?: string | null
  /** Letzter Tag der Eingewöhnung, frei gewählt. Regulär ab dem Tag danach. */
  eingewoehnungBis?: string | null
  posten: Betreuungsposten[]
}

export interface Betreuungsverhaeltnis extends Betreuungseingabe {
  id: string
  /** Aus `customers` gelesen – keine Kopie am Verhältnis. */
  kundenname: string
  ende: string | null
  version: number
}

export interface Betreuungsposition {
  code: string
  bezeichnung: string
  menge: number
  einheit: string
  einzelpreisRp: number
  betragRp: number
}

export interface Betreuungsvorschlag {
  verhaeltnisId: string
  kindName: string
  kundenname: string
  customerId: string
  kategorie: 'baby' | 'kleinkind'
  positionen: Betreuungsposition[]
  nettoRp: number
  hinweise: string[]
  /** Der Zeitraum der Eingewöhnung – oder `null`. */
  eingewoehnung: {
    beginn: string
    ende: string
    regulaerAb: string
    tage: number
  } | null
  /** Liegt der Monat vollständig in der Eingewöhnung? Dann keine Pauschale. */
  nurEingewoehnung: boolean
  /** Der erste reguläre Tag, wenn dieser Monat anteilig gerechnet ist. */
  anteiligAb: string | null
  vorhanden: {
    documentId: string
    nummer: string | null
    status: string
    totalRp: number
    nettoRp: number
    /** Summe der nicht stornierten Zahlungen, in Rappen. */
    bezahltRp: number
    /**
     * Der Zahlungsstand – aus derselben Rechnung wie überall sonst.
     * `null` an einem Entwurf: Er ist keine Forderung.
     */
    zahlungsstatus:
      | 'offen'
      | 'teilbezahlt'
      | 'bezahlt'
      | 'ueberbezahlt'
      | 'storniert'
      | null
    /** Der Beleg, den dieser hier ersetzt. */
    ersetztBelegId: string | null
    ersetztNummer: string | null
    /** Der Beleg, der diesen hier ersetzt hat. */
    ersetztDurch: { documentId: string; nummer: string | null } | null
  } | null
  /** Darf die bestehende Rechnung ersetzt werden? */
  korrigierbar: boolean
  /** Warum nicht. Leer, wenn es geht. */
  korrekturGrund: string
  /**
   * Abweichung zwischen Entwurf und frischer Rechnung, in Rappen.
   * `null` heisst: kein offener Entwurf. `0` heisst: einig.
   */
  abweichungRp: number | null
  /** Darf der Entwurf jetzt entstehen? Der Server entscheidet, nicht die Liste. */
  erzeugbar: boolean
  grund: string
}

/** Die Zahlen über dem Monat. Sie kommen aus derselben Quelle wie die Liste. */
export interface Betreuungszahlen {
  vorgesehen: number
  gesamtRp: number
  bereitZurFreigabe: number
  offenVorzubereiten: number
  abgeschlossen: number
  storniert: number
  brauchtPruefung: number
  abweichend: number
}

export interface Betreuungsmonat {
  monat: { jahr: number; monat: number }
  von: string
  bis: string
  bezeichnung: string
  vorschlaege: Betreuungsvorschlag[]
  zahlen: Betreuungszahlen
}

/** Was aus einer Sammelfreigabe zurückkommt – je Rechnung eine Zeile. */
export interface BetreuungAbschluss {
  monat: { jahr: number; monat: number }
  abgeschlossen: number
  warSchon: number
  fehler: number
  ergebnisse: Array<{
    documentId: string
    ergebnis: 'abgeschlossen' | 'war-schon' | 'fehler'
    nummer?: string | null
    meldung?: string
  }>
}

/* ═══════════════════════════════════════════════════════════════════════════
   Die Kita-Gemeinschaft
   ═══════════════════════════════════════════════════════════════════════════ */

/** Wie die Gemeinschaft rechtlich aufgestellt ist. */
export type Rechtsform = 'gemeinsame_firma' | 'selbstaendig'

export interface Dachorganisation {
  id: string
  name: string
  rechtsform: Rechtsform
  street: string
  zip: string
  city: string
  email: string
  phone: string
  logoKey: string | null
}

export interface Dacheingabe {
  name: string
  rechtsform: Rechtsform
  street: string
  zip: string
  city: string
  email: string
  phone: string
}

/**
 * Ein Mitglied – Name, Ort, Stand.
 *
 * Mehr steht hier nicht, und mehr liefert der Server auch nicht. Umsatz,
 * Kinderzahl, offene Posten oder IBAN eines anderen Betriebs gibt es in
 * dieser Ansicht nicht; dafür braucht es eine Berechtigung im Betrieb selbst.
 */
export interface Dachmitglied {
  tenantId: string
  name: string
  city: string
  status: 'aktiv' | 'ausgetreten'
  beigetretenAm: string
  verlassenAm: string | null
  eigener: boolean
}

export interface Dacheinladung {
  id: string
  name: string
  email: string
  status: 'offen' | 'eingeloest' | 'zurueckgezogen'
  gueltigBis: string
  erstelltAm: string
  tenantId: string | null
}

export interface Dachverwalter {
  userId: string
  name: string
  email: string
  eigener: boolean
}

export interface Dachsicht {
  dach: Dachorganisation | null
  darfVerwalten?: boolean
  mitglieder?: Dachmitglied[]
  einladungen?: Dacheinladung[]
  verwalter?: Dachverwalter[]
}

/** Was auf der Einladungsseite steht – ohne Anmeldung lesbar. */
export interface Einladungsansicht {
  name: string
  email: string
  kita: string
  rechtsform: Rechtsform
  ort: string
  /** Der gemeinsame Briefkopf – dieselbe Adresse, die auf jeder Rechnung steht. */
  street: string
  zip: string
  kitaEmail: string
  kitaPhone: string
  hatLogo: boolean
}
